import { c as _c } from "react/compiler-runtime";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/migration-steps-accordion";
import { Accordion as WorkflowAccordion, AccordionContent as WorkflowAccordionContent, AccordionItem as WorkflowAccordionItem, AccordionTrigger as WorkflowAccordionTrigger } from "@/components/ui/migration-workflows-accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover-dialog";
import { CheckCircleIcon, ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { ArrowLeft, ArrowRight, Check, ChevronsUpDown, Github, Loader2, MoveRight, X } from "lucide-react";
import React, { Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { cn } from "@/lib/utils";
import { BlackGitHubIcon, WhiteGitHubIcon } from "./GitHubIcon";
import { useTheme } from "next-themes";
import { ScrollArea } from "@/components/ui/scroll-area";
import { type WorkflowFileData, type WorkflowFileType, type DockerJobs, useJobsForWorkflow, useDockerJobsForWorkflow, useMigrateRunnersForRepository, useRunsOnFilesForRepository } from "@/hooks/useMigrateRunners";
import { toggleAutofixErrorMessage, useToggleAutofix } from "@/hooks/useToggleAutofix";
import { toast } from "sonner";
import type { AxiosError } from "axios";
import { Skeleton } from "./ui/skeleton";
import { atomFamily } from "jotai/utils";
import { atom, useAtom, useAtomValue } from "jotai";
import { atomWithImmer } from "jotai-immer";
import { useActiveGithubOrgForUser, useIsPersonalOrg } from "@/hooks/useConnectedGithubOrgs";
import Link from "next/link";
import { LoadingSpinner } from "./ui/loading-spinner";
import { BSErrorBoundary } from "./ui/bs-error-boundary";
import { usePostHog } from "posthog-js/react";
import { MIGRATION_WIZARD_EVENTS } from "@/lib/posthog-constants";
import { searchRepositories, useRepositorySearch } from "@/lib/repositories";
import { useApi } from "@/hooks/useApi";
import { useIsFetching, useQuery, useQueryClient } from "react-query";
import { useDebounceValue } from "usehooks-ts";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { Switch } from "./ui/switch";
import { shuffle } from "lodash";
import { AutofixToggle, PrCard } from "./agent-session/PrCard";
import type { ManagedPr } from "./agent-session/types";
import { CodesmithIcon } from "./icons/CodesmithIcon";
import Image from "next/image";
import MigrationWizardSvg from "@/assets/migration-wizard.svg";
import { useSidebar } from "./ui/sidebar";
import { useRunnerRegion } from "@/hooks/useRunnerRegion";
const RUNNER_OPTIONS = [
// Ubuntu 24.04
"blacksmith-2vcpu-ubuntu-2404", "blacksmith-4vcpu-ubuntu-2404", "blacksmith-8vcpu-ubuntu-2404", "blacksmith-16vcpu-ubuntu-2404", "blacksmith-32vcpu-ubuntu-2404",
// Ubuntu 22.04
"blacksmith-2vcpu-ubuntu-2204", "blacksmith-4vcpu-ubuntu-2204", "blacksmith-8vcpu-ubuntu-2204", "blacksmith-16vcpu-ubuntu-2204", "blacksmith-32vcpu-ubuntu-2204",
// Ubuntu 24.04 ARM
"blacksmith-2vcpu-ubuntu-2404-arm", "blacksmith-4vcpu-ubuntu-2404-arm", "blacksmith-8vcpu-ubuntu-2404-arm", "blacksmith-16vcpu-ubuntu-2404-arm", "blacksmith-32vcpu-ubuntu-2404-arm",
// Ubuntu 22.04 ARM
"blacksmith-2vcpu-ubuntu-2204-arm", "blacksmith-4vcpu-ubuntu-2204-arm", "blacksmith-8vcpu-ubuntu-2204-arm", "blacksmith-16vcpu-ubuntu-2204-arm", "blacksmith-32vcpu-ubuntu-2204-arm",
// Windows 2025
"blacksmith-2vcpu-windows-2025", "blacksmith-4vcpu-windows-2025", "blacksmith-8vcpu-windows-2025", "blacksmith-16vcpu-windows-2025", "blacksmith-32vcpu-windows-2025",
// macOS
"blacksmith-6vcpu-macos-latest", "blacksmith-6vcpu-macos-15", "blacksmith-6vcpu-macos-26", "blacksmith-12vcpu-macos-latest", "blacksmith-12vcpu-macos-15", "blacksmith-12vcpu-macos-26"] as const;
type ResolvedTheme = string | undefined;
type RunnerPaths = Record<string, Record<string, string[]>>;

/**
 * A job's runner list is a flattened list of [current, target] pairs, so a job
 * whose matrix mixes several runner labels carries more than one pair,
 * e.g. ["depot-22.04", "blacksmith-2204", "depot-22.04-arm", "blacksmith-2204-arm"].
 */
function toRunnerPairs(runners: string[]): [string, string][] {
  const pairs: [string, string][] = [];
  for (let i = 0; i + 1 < runners.length; i += 2) {
    pairs.push([runners[i], runners[i + 1]]);
  }
  return pairs;
}

/** Body copy for the migration PR success card (PrCard description). */
const MIGRATION_PR_SUCCESS_CARD_DESCRIPTION = "Blacksmith is the fastest way to run your GitHub Actions.";
function hasRunnerChanges(paths: RunnerPaths): boolean {
  return Object.values(paths).some(jobs => Object.values(jobs).some(runners => toRunnerPairs(runners).some(([current, target]) => current !== target)));
}
function ThemedGitHubIcon(t0) {
  const $ = _c(2);
  const {
    theme
  } = t0;
  let t1;
  if ($[0] !== theme) {
    t1 = theme === "dark" ? <BlackGitHubIcon /> : <WhiteGitHubIcon />;
    $[0] = theme;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
function ThemedGitHubIconLight(t0) {
  const $ = _c(2);
  const {
    theme
  } = t0;
  let t1;
  if ($[0] !== theme) {
    t1 = theme === "dark" ? <WhiteGitHubIcon /> : <BlackGitHubIcon />;
    $[0] = theme;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
const pullRequestLoadingMessages = ["Wrangling some YAML...", "Moving mountains...", "Conjuring some magic...", "Polishing workflows..."] as const;
type Step = "repository" | "runners" | "docker-caching" | "pull-request";

/** When more than this many repos are selected, skip steps 2 & 3 and auto-migrate with defaults. */
const AUTO_MIGRATE_THRESHOLD = 4;

/**
 * Hard cap on how many repos can be migrated in a single batch. Large orgs can
 * have tens of thousands of repos; without a cap, "Select all" would fan out
 * that many concurrent job-fetch queries and wedge the wizard.
 */
const MAX_MIGRATE_REPOS = 20;
function ErrorFallback(t0) {
  const $ = _c(2);
  const {
    error
  } = t0;
  if (error?.response?.status === 403) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <DialogHeader><DialogTitle /><DialogDescription /></DialogHeader>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <>{t1}<div><div className="flex items-center justify-center h-[150px] w-full"><ExclamationCircleIcon className="w-12 h-12 text-red-500" /></div></div></>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  throw error;
}
function MigrationWizardSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col h-[300px] w-full gap-4"><Skeleton className="h-8 w-48 rounded-lg" /><Skeleton className="h-6 w-96 mb-4 rounded-lg" /><Skeleton className="w-full h-32 rounded-lg" /><Skeleton className="w-full h-12 rounded-lg" /><Skeleton className="w-full h-12 rounded-lg" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
export const migrationWizardOpenAtom = atomWithImmer<boolean>(false);
export const migrationWizardStepAtom = atom<Step>("repository");
export default function MigrationWizard(t0) {
  const $ = _c(39);
  const [open, setOpen] = useAtom(migrationWizardOpenAtom);
  const [step, setStep] = useAtom(migrationWizardStepAtom);
  const posthog = usePostHog();
  const api = useApi();
  const queryClient = useQueryClient();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const {
    isPersonalOrg
  } = useIsPersonalOrg();
  const {
    state: sidebarState,
    isMobile
  } = useSidebar();
  let t1;
  let t2;
  if ($[0] !== open || $[1] !== setStep) {
    t1 = () => {
      if (open) {
        setStep("repository");
      }
    };
    t2 = [open, setStep];
    $[0] = open;
    $[1] = setStep;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  const canClose = step === "repository" || step === "pull-request";
  const t3 = isPersonalOrg ? "cursor-not-allowed opacity-60 hover:bg-transparent hover:text-[#434546] dark:hover:bg-transparent dark:hover:text-[#bbb]" : "cursor-pointer";
  let t4;
  if ($[4] !== t3) {
    t4 = cn("group/migwiz mb-0 flex h-8 w-full items-center gap-2 rounded-sm p-1.5 text-left type-body-s-regular", "text-[#434546] hover:bg-[#0000000f] hover:text-[#161718]", "dark:text-[#bbb] dark:hover:bg-white/6 dark:hover:text-white", "outline-hidden border border-transparent focus-visible:!border-2 focus-visible:!border-[var(--focus-border)] focus-visible:ring-0", "group-data-[state=collapsed]:justify-center group-data-[state=collapsed]:p-0 group-data-[state=collapsed]:!w-8 group-data-[state=collapsed]:!h-8 group-data-[state=collapsed]:!max-w-8 group-data-[state=collapsed]:!min-w-8 group-data-[state=collapsed]:mx-auto group-data-[collapsible=icon]:!w-8 group-data-[collapsible=icon]:!h-8 group-data-[collapsible=icon]:!max-w-8 group-data-[collapsible=icon]:!min-w-8", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== isPersonalOrg || $[7] !== posthog) {
    t5 = e => {
      if (isPersonalOrg) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      posthog?.capture(MIGRATION_WIZARD_EVENTS.OPEN);
    };
    $[6] = isPersonalOrg;
    $[7] = posthog;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== activeGithubOrg || $[10] !== api || $[11] !== isPersonalOrg || $[12] !== queryClient) {
    t6 = () => {
      if (activeGithubOrg && !isPersonalOrg) {
        queryClient.prefetchQuery(["repositorySearch", activeGithubOrg, "", 100], () => searchRepositories({
          api,
          endpoint: `user/github/orgs/${activeGithubOrg}/migrate-runners/repositories`,
          search: "",
          perPage: 100
        }), {
          staleTime: 30000
        });
      }
    };
    $[9] = activeGithubOrg;
    $[10] = api;
    $[11] = isPersonalOrg;
    $[12] = queryClient;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  let t8;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Image src={MigrationWizardSvg} alt="" aria-hidden="true" width={18} height={18} className="size-[18px] shrink-0" />;
    t8 = <span className="whitespace-nowrap group-data-[state=collapsed]:hidden">Migration Wizard</span>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t7 = $[14];
    t8 = $[15];
  }
  let t9;
  if ($[16] !== isPersonalOrg || $[17] !== t4 || $[18] !== t5 || $[19] !== t6) {
    t9 = <DialogTrigger asChild={true}><TooltipTrigger asChild={true}><button type="button" aria-disabled={isPersonalOrg} className={t4} onClick={t5} onMouseEnter={t6}>{t7}{t8}</button></TooltipTrigger></DialogTrigger>;
    $[16] = isPersonalOrg;
    $[17] = t4;
    $[18] = t5;
    $[19] = t6;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  const t10 = sidebarState !== "collapsed" || isMobile;
  const t11 = isPersonalOrg ? "Blacksmith doesn't support personal orgs" : "Migration Wizard";
  let t12;
  if ($[21] !== t10 || $[22] !== t11) {
    t12 = <TooltipContent side="right" align="center" hidden={t10}>{t11}</TooltipContent>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12 || $[25] !== t9) {
    t13 = <Tooltip delayDuration={0}>{t9}{t12}</Tooltip>;
    $[24] = t12;
    $[25] = t9;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  let t15;
  if ($[27] !== canClose) {
    t14 = e_0 => {
      const target = e_0.detail.originalEvent.target as Element | null;
      if (target?.closest("[data-radix-popper-content-wrapper]")) {
        e_0.preventDefault();
        return;
      }
      if (!canClose) {
        e_0.preventDefault();
      }
    };
    t15 = e_1 => {
      if (!canClose) {
        e_1.preventDefault();
      }
    };
    $[27] = canClose;
    $[28] = t14;
    $[29] = t15;
  } else {
    t14 = $[28];
    t15 = $[29];
  }
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <BSErrorBoundary title="Migration Wizard" FallbackComponent={ErrorFallback}><Suspense fallback={<MigrationWizardSkeleton />}><MigrationWizardContent /></Suspense></BSErrorBoundary>;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t14 || $[32] !== t15) {
    t17 = <DialogContent className="w-[760px] max-w-[760px] max-h-[90vh] overflow-y-auto" onInteractOutside={t14} onEscapeKeyDown={t15}>{t16}</DialogContent>;
    $[31] = t14;
    $[32] = t15;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== open || $[35] !== setOpen || $[36] !== t13 || $[37] !== t17) {
    t18 = <Dialog open={open} onOpenChange={setOpen}>{t13}{t17}</Dialog>;
    $[34] = open;
    $[35] = setOpen;
    $[36] = t13;
    $[37] = t17;
    $[38] = t18;
  } else {
    t18 = $[38];
  }
  return t18;
}
const runnerPathsForRepo = atomFamily((repositoryName: string) => atomWithImmer<Record<string, Record<string, string[]>>>({}));

// Atom to track which jobs should have Docker caching migrated
const dockerCachingJobsForRepo = atomFamily((repositoryName: string) => atomWithImmer<Record<string, Record<string, boolean>>>({}));

// Atom to track Docker jobs loading state
export const dockerJobsLoadingAtom = atom<number>(0);
export const dockerJobsAreLoadingAtom = atom(get => get(dockerJobsLoadingAtom) > 0);
function RotatingLoadingMessage() {
  const $ = _c(6);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = shuffle([...pullRequestLoadingMessages]);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const shuffledMessages = t0;
  const [messageIndex, setMessageIndex] = useState(0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (shuffledMessages.length <= 1) {
        return;
      }
      const interval = window.setInterval(() => {
        setMessageIndex(prev => (prev + 1) % shuffledMessages.length);
      }, 2500);
      return () => window.clearInterval(interval);
    };
    t2 = [shuffledMessages];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  const message = shuffledMessages[messageIndex];
  const t3 = `${message}-${messageIndex}`;
  let t4;
  if ($[3] !== message || $[4] !== t3) {
    t4 = <div className="max-w-[400px] mx-auto mt-1 h-5 type-body-s-regular text-neutral-500 dark:text-neutral-400" aria-live="polite" aria-atomic="true"><span key={t3} className="inline-block animate-rotating-loading-pop">{message}</span></div>;
    $[3] = message;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  return t4;
}

// Per-repo atom shortcuts — one line wrappers for readability at call sites.
function useRunnerPathsForRepo(repo) {
  const $ = _c(2);
  let t0;
  if ($[0] !== repo) {
    t0 = runnerPathsForRepo(repo);
    $[0] = repo;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useAtom(t0);
}
function useDockerCachingJobsForRepo(repo) {
  const $ = _c(2);
  let t0;
  if ($[0] !== repo) {
    t0 = dockerCachingJobsForRepo(repo);
    $[0] = repo;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useAtom(t0);
}

/**
 * Bundles all multi-repo PR-generation state: which repos have a PR, which
 * errored, which were skipped (no runner changes), and which are in flight.
 * Returns stable callbacks so child components can be memoized.
 */
function useMultiRepoPrState() {
  const $ = _c(23);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {};
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [prUrls, setPrUrls] = useState(t0);
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [prErrors, setPrErrors] = useState(t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [managedPrIds, setManagedPrIds] = useState(t2);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {};
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [sessionIds, setSessionIds] = useState(t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const [autofixStates, setAutofixStates] = useState(t4);
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = new Set();
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const [generatingRepos, setGeneratingRepos] = useState(t5);
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = new Set();
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const [skippedRepos, setSkippedRepos] = useState(t6);
  const [batchStarted, setBatchStarted] = useState(false);
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = repo => {
      setGeneratingRepos(prev => {
        if (!prev.has(repo)) {
          return prev;
        }
        const next = new Set(prev);
        next.delete(repo);
        return next;
      });
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const clearGenerating = t7;
  let t8;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = (repo_0, url, managedPrId, sessionId) => {
      setPrUrls(prev_0 => ({
        ...prev_0,
        [repo_0]: url
      }));
      setManagedPrIds(prev_1 => ({
        ...prev_1,
        [repo_0]: managedPrId
      }));
      setSessionIds(prev_2 => ({
        ...prev_2,
        [repo_0]: sessionId
      }));
      setAutofixStates(prev_3 => ({
        ...prev_3,
        [repo_0]: prev_3[repo_0] ?? {
          enabled: !!managedPrId
        }
      }));
      clearGenerating(repo_0);
    };
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  const onPrGenerated = t8;
  let t9;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = (repo_1, error) => {
      setPrErrors(prev_4 => ({
        ...prev_4,
        [repo_1]: error
      }));
      clearGenerating(repo_1);
    };
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const onPrError = t9;
  let t10;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = repo_2 => {
      setGeneratingRepos(prev_5 => new Set(prev_5).add(repo_2));
    };
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const onRepoGenerating = t10;
  let t11;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = repo_3 => {
      setSkippedRepos(prev_6 => new Set(prev_6).add(repo_3));
    };
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  const onRepoSkipped = t11;
  let t12;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setBatchStarted(true);
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  const startBatch = t12;
  let t13;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = (repos, enabled) => {
      setAutofixStates(prev_7 => {
        const next_0 = {
          ...prev_7
        };
        for (const repo_4 of repos) {
          next_0[repo_4] = {
            enabled
          };
        }
        return next_0;
      });
    };
    $[13] = t13;
  } else {
    t13 = $[13];
  }
  const onToggleAllAutofix = t13;
  let t14;
  if ($[14] !== autofixStates || $[15] !== batchStarted || $[16] !== generatingRepos || $[17] !== managedPrIds || $[18] !== prErrors || $[19] !== prUrls || $[20] !== sessionIds || $[21] !== skippedRepos) {
    t14 = {
      prUrls,
      prErrors,
      managedPrIds,
      sessionIds,
      autofixStates,
      generatingRepos,
      skippedRepos,
      batchStarted,
      onPrGenerated,
      onPrError,
      onRepoGenerating,
      onRepoSkipped,
      startBatch,
      onToggleAllAutofix
    };
    $[14] = autofixStates;
    $[15] = batchStarted;
    $[16] = generatingRepos;
    $[17] = managedPrIds;
    $[18] = prErrors;
    $[19] = prUrls;
    $[20] = sessionIds;
    $[21] = skippedRepos;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  return t14;
}

// Silent error boundary — renders nothing on error so preloaders don't crash the wizard
class SilentErrorBoundary extends React.Component<{
  children: React.ReactNode;
}, {
  hasError: boolean;
}> {
  constructor(props: {
    children: React.ReactNode;
  }) {
    super(props);
    this.state = {
      hasError: false
    };
  }
  static getDerivedStateFromError() {
    return {
      hasError: true
    };
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/**
 * Eagerly fetches everything the wizard might need for a repo (workflows,
 * jobs, docker-job detection) and primes the runner-paths atom with the
 * API's suggested migrations. This runs invisibly from step 1 so that by
 * the time the user reaches step 2, 3, or 4 the data is already there.
 */
function AutoMigrateRepoPreloader(t0) {
  const $ = _c(9);
  const {
    repo,
    seedDockerCaching
  } = t0;
  const [, setUpdatedRunnerPaths] = useRunnerPathsForRepo(repo);
  const [, setDockerCachingJobs] = useDockerCachingJobsForRepo(repo);
  const {
    workflows,
    reusableWorkflows
  } = useRunsOnFilesForRepository(repo);
  let t1;
  if ($[0] !== reusableWorkflows || $[1] !== workflows) {
    t1 = [...workflows, ...reusableWorkflows];
    $[0] = reusableWorkflows;
    $[1] = workflows;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const files = t1;
  let t2;
  if ($[3] !== files || $[4] !== repo || $[5] !== seedDockerCaching || $[6] !== setDockerCachingJobs || $[7] !== setUpdatedRunnerPaths) {
    t2 = <>{files.map(file => <WorkflowPreloader key={file.path} repo={repo} workflowPath={file.path} setUpdatedRunnerPaths={setUpdatedRunnerPaths} setDockerCachingJobs={setDockerCachingJobs} seedDockerCaching={seedDockerCaching} />)}</>;
    $[3] = files;
    $[4] = repo;
    $[5] = seedDockerCaching;
    $[6] = setDockerCachingJobs;
    $[7] = setUpdatedRunnerPaths;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
}

/**
 * Fetches jobs for a single workflow, seeds the runner-paths atom with
 * the suggested migrations, and warms the docker-jobs react-query cache
 * so the docker step doesn't need to refetch.
 */
function WorkflowPreloader({
  repo,
  workflowPath,
  setUpdatedRunnerPaths,
  setDockerCachingJobs,
  seedDockerCaching
}: {
  repo: string;
  workflowPath: string;
  setUpdatedRunnerPaths: (fn: (draft: RunnerPaths) => void) => void;
  setDockerCachingJobs: (fn: (draft: Record<string, Record<string, boolean>>) => void) => void;
  seedDockerCaching: boolean;
}) {
  const {
    jobs,
    isLoading,
    error
  } = useJobsForWorkflow({
    workflowPath,
    repositoryName: repo
  });

  // Pin the latest setters so they don't cause effect re-runs.
  const runnerSetterRef = useRef(setUpdatedRunnerPaths);
  runnerSetterRef.current = setUpdatedRunnerPaths;
  const dockerSetterRef = useRef(setDockerCachingJobs);
  dockerSetterRef.current = setDockerCachingJobs;
  const [initialized, setInitialized] = useState(false);

  // Seed the runner-paths atom with [current, suggested] pairs — once.
  useEffect(() => {
    if (initialized || isLoading || error || !jobs) return;
    const entries = Object.entries(jobs);
    if (entries.length > 0) {
      runnerSetterRef.current(draft => {
        for (const [jobName, runners] of entries) {
          if (!Array.isArray(runners)) continue;
          draft[workflowPath] ??= {};
          draft[workflowPath][jobName] ??= [...runners];
        }
      });
    }
    setInitialized(true);
  }, [jobs, isLoading, error, workflowPath, initialized]);

  // Docker jobs depend on which runners the user plans to migrate.
  const plannedMigrations = useMemo(() => {
    if (isLoading || error || !jobs) return [];
    return Object.entries(jobs).filter(([, r]) => Array.isArray(r) && toRunnerPairs(r).some(([current, target]) => current !== target)).map(([name]) => name);
  }, [jobs, isLoading, error]);

  // Fetch docker jobs — the hook warms react-query's cache.
  const {
    jobs: dockerJobs,
    isLoading: dockerLoading
  } = useDockerJobsForWorkflow({
    workflowPath,
    repositoryName: repo,
    plannedMigrations
  });

  // Seed the docker-caching atom with `true` for every detected docker
  // build job so the PR includes docker migration even when the user
  // never opens the docker step (e.g. "Migrate all" with >4 repos).
  // Only seed when `seedDockerCaching` is true — in the manual flow the
  // DockerCachingStep handles initialization with its own `defaultEnabled`.
  const [dockerInitialized, setDockerInitialized] = useState(false);
  useEffect(() => {
    if (!seedDockerCaching || dockerInitialized || dockerLoading || !dockerJobs) return;
    const detected = Object.entries(dockerJobs).filter(([, data]) => data.dockerBuild?.detected).map(([name_0]) => name_0);
    if (detected.length > 0) {
      dockerSetterRef.current(draft_0 => {
        draft_0[workflowPath] ??= {};
        for (const jobName_0 of detected) {
          draft_0[workflowPath][jobName_0] ??= true;
        }
      });
    }
    setDockerInitialized(true);
  }, [seedDockerCaching, dockerJobs, dockerLoading, workflowPath, dockerInitialized]);
  return null;
}
function RunnerSelectionStepSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div>{Array.from({
        length: 5
      }).map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Wires the runner-selection step for a single repo to its jotai atom. */
function _temp(_, i) {
  return <Skeleton key={i} className="w-full h-11 rounded-none border-b" />;
}
function RunnerStepContent(t0) {
  const $ = _c(9);
  const {
    repo,
    openDropdownId,
    setOpenDropdownId
  } = t0;
  const [updatedRunnerPaths, setUpdatedRunnerPaths] = useRunnerPathsForRepo(repo);
  let t1;
  if ($[0] !== setUpdatedRunnerPaths) {
    t1 = (workflow, job, pairIndex, updatedRunner, currentRunner) => {
      setUpdatedRunnerPaths(draft => {
        const runners = [...(draft[workflow]?.[job] ?? [])];
        runners[pairIndex * 2] = currentRunner ?? runners[pairIndex * 2];
        runners[pairIndex * 2 + 1] = updatedRunner;
        draft[workflow] = {
          ...draft[workflow],
          [job]: runners
        };
      });
    };
    $[0] = setUpdatedRunnerPaths;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const updateRunnerPath = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RunnerSelectionStepSkeleton />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== openDropdownId || $[4] !== repo || $[5] !== setOpenDropdownId || $[6] !== updateRunnerPath || $[7] !== updatedRunnerPaths) {
    t3 = <div className="text-sm"><Suspense fallback={t2}><RunnerSelectionStep selectedRepository={repo} updateRunnerPath={updateRunnerPath} updatedRunnerPaths={updatedRunnerPaths} openDropdownId={openDropdownId} setOpenDropdownId={setOpenDropdownId} /></Suspense></div>;
    $[3] = openDropdownId;
    $[4] = repo;
    $[5] = setOpenDropdownId;
    $[6] = updateRunnerPath;
    $[7] = updatedRunnerPaths;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}

/** Wires the docker-caching step for a single repo to its jotai atoms. */
function DockerStepContent(t0) {
  const $ = _c(6);
  const {
    repo,
    autoMigrate
  } = t0;
  const [updatedRunnerPaths] = useRunnerPathsForRepo(repo);
  const [dockerCachingJobs, setDockerCachingJobs] = useDockerCachingJobsForRepo(repo);
  let t1;
  if ($[0] !== autoMigrate || $[1] !== dockerCachingJobs || $[2] !== repo || $[3] !== setDockerCachingJobs || $[4] !== updatedRunnerPaths) {
    t1 = <div className="text-sm"><DockerCachingStep selectedRepository={repo} dockerCachingJobs={dockerCachingJobs} setDockerCachingJobs={setDockerCachingJobs} updatedRunnerPaths={updatedRunnerPaths} defaultEnabled={autoMigrate} /></div>;
    $[0] = autoMigrate;
    $[1] = dockerCachingJobs;
    $[2] = repo;
    $[3] = setDockerCachingJobs;
    $[4] = updatedRunnerPaths;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
}
function RepoPaginationHeader(t0) {
  const $ = _c(17);
  const {
    repositories,
    currentIndex
  } = t0;
  const repo = repositories[currentIndex];
  const {
    resolvedTheme
  } = useTheme();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  if (repositories.length <= 1) {
    return null;
  }
  const t1 = currentIndex + 1;
  let t2;
  if ($[0] !== repositories.length || $[1] !== t1) {
    t2 = <div className="type-body-xxs-regular uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Repository {t1} of {repositories.length}</div>;
    $[0] = repositories.length;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== resolvedTheme) {
    t3 = <span className="inline-flex shrink-0 [&_img]:!w-4 [&_img]:!h-4"><ThemedGitHubIconLight theme={resolvedTheme} /></span>;
    $[3] = resolvedTheme;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== activeGithubOrg || $[6] !== repo) {
    t4 = <span className="type-body-s-medium">{activeGithubOrg}/{repo}</span>;
    $[5] = activeGithubOrg;
    $[6] = repo;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t3 || $[9] !== t4) {
    t5 = <div className="flex items-center gap-1.5 mt-0.5">{t3}{t4}</div>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t2 || $[12] !== t5) {
    t6 = <div>{t2}{t5}</div>;
    $[11] = t2;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="flex items-center gap-1.5 text-neutral-500 dark:text-neutral-400"><kbd className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-sm border border-[var(--color-border)] bg-neutral-100 dark:bg-neutral-800 type-body-xxs-regular font-mono text-neutral-500 dark:text-neutral-400">Tab</kbd><span className="type-body-xxs-regular">jump</span><kbd className="inline-flex items-center justify-center h-5 w-5 rounded-sm border border-[var(--color-border)] bg-neutral-100 dark:bg-neutral-800 type-body-xxs-regular font-mono text-neutral-500 dark:text-neutral-400">←</kbd><kbd className="inline-flex items-center justify-center h-5 w-5 rounded-sm border border-[var(--color-border)] bg-neutral-100 dark:bg-neutral-800 type-body-xxs-regular font-mono text-neutral-500 dark:text-neutral-400">→</kbd><span className="type-body-xxs-regular">repo</span></div>;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t6) {
    t8 = <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--color-border)] bg-[var(--color-surface)] text-left">{t6}{t7}</div>;
    $[15] = t6;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  return t8;
}
function RepoPaginationFooter(t0) {
  const $ = _c(41);
  const {
    repositories,
    currentIndex,
    setCurrentIndex,
    onBack,
    onNext,
    isLoading
  } = t0;
  const isMultiRepo = repositories.length > 1;
  const isLastRepo = currentIndex >= repositories.length - 1;
  const isFirstRepo = currentIndex === 0;
  const refocusContent = _temp3;
  let t1;
  let t2;
  if ($[0] !== currentIndex || $[1] !== isFirstRepo || $[2] !== isLastRepo || $[3] !== isMultiRepo || $[4] !== setCurrentIndex) {
    t1 = () => {
      if (!isMultiRepo) {
        return;
      }
      const handler = e => {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") {
          return;
        }
        if (e.key === "ArrowLeft" && !isFirstRepo) {
          setCurrentIndex(currentIndex - 1);
          refocusContent();
        } else {
          if (e.key === "ArrowRight" && !isLastRepo) {
            setCurrentIndex(currentIndex + 1);
            refocusContent();
          }
        }
      };
      window.addEventListener("keydown", handler);
      return () => window.removeEventListener("keydown", handler);
    };
    t2 = [isMultiRepo, isFirstRepo, isLastRepo, currentIndex, setCurrentIndex, refocusContent];
    $[0] = currentIndex;
    $[1] = isFirstRepo;
    $[2] = isLastRepo;
    $[3] = isMultiRepo;
    $[4] = setCurrentIndex;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== currentIndex || $[8] !== isFirstRepo || $[9] !== isMultiRepo || $[10] !== onBack || $[11] !== setCurrentIndex) {
    t3 = () => {
      if (isMultiRepo && !isFirstRepo) {
        setCurrentIndex(currentIndex - 1);
        refocusContent();
      } else {
        onBack();
      }
    };
    $[7] = currentIndex;
    $[8] = isFirstRepo;
    $[9] = isMultiRepo;
    $[10] = onBack;
    $[11] = setCurrentIndex;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  let t4;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <ArrowLeft className="w-4 h-4 mr-2" />;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t3) {
    t5 = <Button onClick={t3} variant="secondary">{t4} Back</Button>;
    $[14] = t3;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== currentIndex || $[17] !== isMultiRepo || $[18] !== repositories) {
    t6 = isMultiRepo && <div className="absolute inset-0 flex items-center justify-center pointer-events-none">{repositories.length <= 10 ? <div className="flex items-center gap-1.5">{repositories.map((_, i) => {
          let dotColor;
          if (i === currentIndex) {
            dotColor = "bg-neutral-900 dark:bg-neutral-100";
          } else {
            if (i < currentIndex) {
              dotColor = "bg-neutral-400";
            } else {
              dotColor = "bg-neutral-300 dark:bg-neutral-600";
            }
          }
          return <div key={i} className={cn("w-1.5 h-1.5 rounded-full transition-colors", dotColor)} />;
        })}</div> : <span className="type-body-xs-regular text-neutral-500 dark:text-neutral-400">{currentIndex + 1} / {repositories.length}</span>}</div>;
    $[16] = currentIndex;
    $[17] = isMultiRepo;
    $[18] = repositories;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== currentIndex || $[21] !== isLastRepo || $[22] !== isMultiRepo || $[23] !== onNext || $[24] !== setCurrentIndex) {
    t7 = () => {
      if (isMultiRepo && !isLastRepo) {
        setCurrentIndex(currentIndex + 1);
        refocusContent();
      } else {
        onNext();
      }
    };
    $[20] = currentIndex;
    $[21] = isLastRepo;
    $[22] = isMultiRepo;
    $[23] = onNext;
    $[24] = setCurrentIndex;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== isLoading) {
    t8 = isLoading && <Loader2 className="h-4 w-4 animate-spin" />;
    $[26] = isLoading;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  let t9;
  if ($[28] !== isLastRepo || $[29] !== isLoading || $[30] !== isMultiRepo) {
    t9 = !isLoading && <>{isMultiRepo && !isLastRepo ? <>Next repository <ArrowRight className="w-4 h-4 ml-2" /></> : <>Next <ArrowRight className="w-4 h-4 ml-2" /></>}</>;
    $[28] = isLastRepo;
    $[29] = isLoading;
    $[30] = isMultiRepo;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== isLoading || $[33] !== t7 || $[34] !== t8 || $[35] !== t9) {
    t10 = <Button onClick={t7} variant="default" className="w-fit min-w-[90px]" disabled={isLoading}>{t8}{t9}</Button>;
    $[32] = isLoading;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
    $[36] = t10;
  } else {
    t10 = $[36];
  }
  let t11;
  if ($[37] !== t10 || $[38] !== t5 || $[39] !== t6) {
    t11 = <div className="relative flex items-center justify-between p-3">{t5}{t6}{t10}</div>;
    $[37] = t10;
    $[38] = t5;
    $[39] = t6;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  return t11;
}
function _temp3() {
  requestAnimationFrame(_temp2);
}
function _temp2() {
  const content = document.querySelector("[data-state=\"open\"] [data-radix-scroll-area-viewport]");
  const firstFocusable = content?.querySelector("button[role=\"combobox\"], button[data-state]");
  firstFocusable?.focus();
}
export function MigrationWizardContent() {
  const $ = _c(177);
  const posthog = usePostHog();
  const {
    resolvedTheme
  } = useTheme();
  const {
    supportsStickyDisks
  } = useRunnerRegion();
  const {
    multiRepoMigrationWizardEnabled: multiRepoMigrationWizardEnabledFromOrg,
    codesmithEnabled
  } = useActiveGithubOrgForUser();
  const multiRepoMigrationWizardEnabled = process.env.NODE_ENV === "development" ? true : multiRepoMigrationWizardEnabledFromOrg;
  const [rawStep, setStep] = useAtom(migrationWizardStepAtom);
  const step = rawStep === "docker-caching" && !supportsStickyDisks ? "pull-request" : rawStep;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [selectedRepositories, setSelectedRepositories] = useState(t0);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [runnerRepoIndex, setRunnerRepoIndex] = useState(0);
  const [dockerRepoIndex, setDockerRepoIndex] = useState(0);
  let t1;
  if ($[1] !== selectedRepositories) {
    t1 = selectedRepositories.join(",");
    $[1] = selectedRepositories;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const selectedReposKey = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setRunnerRepoIndex(0);
      setDockerRepoIndex(0);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== selectedReposKey) {
    t3 = [selectedReposKey];
    $[4] = selectedReposKey;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useEffect(t2, t3);
  const currentRunnerRepo = selectedRepositories[runnerRepoIndex] ?? "";
  const currentDockerRepo = selectedRepositories[dockerRepoIndex] ?? "";
  const singleRepo = selectedRepositories.length === 1 ? selectedRepositories[0] : "";
  let t4;
  if ($[6] !== singleRepo) {
    t4 = {
      repositoryName: singleRepo
    };
    $[6] = singleRepo;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const {
    doMigrateRunners,
    isLoading,
    prUrl,
    managedPrId: singleRepoManagedPrId,
    sessionId: singleRepoSessionId,
    migrateError
  } = useMigrateRunnersForRepository(t4);
  let t5;
  if ($[8] !== singleRepo) {
    t5 = runnerPathsForRepo(singleRepo);
    $[8] = singleRepo;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const [singleRepoRunnerPaths] = useAtom(t5);
  let t6;
  if ($[10] !== singleRepo) {
    t6 = dockerCachingJobsForRepo(singleRepo);
    $[10] = singleRepo;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const [singleRepoDockerJobs] = useAtom(t6);
  const multiRepoPrState = useMultiRepoPrState();
  const {
    batchStarted
  } = multiRepoPrState;
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = ["jobs"];
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const jobsFetchCount = useIsFetching(t7);
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = ["runs-on-files"];
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const runsOnFilesFetchCount = useIsFetching(t8);
  const jobsOnlyLoading = jobsFetchCount > 0;
  const prGenerationLoading = jobsFetchCount > 0 || runsOnFilesFetchCount > 0;
  const dockerJobsAreLoading = useAtomValue(dockerJobsAreLoadingAtom);
  const isMultiRepo = selectedRepositories.length > 1;
  const isAutoMigrate = multiRepoMigrationWizardEnabled && selectedRepositories.length > AUTO_MIGRATE_THRESHOLD;
  let t9;
  if ($[14] !== setStep) {
    t9 = () => setStep("repository");
    $[14] = setStep;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const goToRepositoryFromRunners = t9;
  let t10;
  if ($[16] !== posthog || $[17] !== setStep || $[18] !== supportsStickyDisks) {
    t10 = () => {
      if (supportsStickyDisks) {
        setStep("docker-caching");
        setDockerRepoIndex(0);
        posthog?.capture(MIGRATION_WIZARD_EVENTS.DOCKER_CACHING_SELECT);
      } else {
        setStep("pull-request");
        posthog?.capture(MIGRATION_WIZARD_EVENTS.PULL_REQUEST_SELECT);
      }
    };
    $[16] = posthog;
    $[17] = setStep;
    $[18] = supportsStickyDisks;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const goFromRunnersToNext = t10;
  let t11;
  if ($[20] !== selectedRepositories.length || $[21] !== setStep) {
    t11 = () => {
      setStep("runners");
      setRunnerRepoIndex(selectedRepositories.length - 1);
    };
    $[20] = selectedRepositories.length;
    $[21] = setStep;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const goFromDockerBack = t11;
  let t12;
  if ($[23] !== posthog || $[24] !== setStep) {
    t12 = () => {
      setStep("pull-request");
      posthog?.capture(MIGRATION_WIZARD_EVENTS.PULL_REQUEST_SELECT);
    };
    $[23] = posthog;
    $[24] = setStep;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const goFromDockerToNext = t12;
  let t13;
  if ($[26] !== isAutoMigrate || $[27] !== posthog || $[28] !== setStep) {
    t13 = () => {
      posthog?.capture(MIGRATION_WIZARD_EVENTS.REPOSITORY_SELECT);
      if (isAutoMigrate) {
        posthog?.capture(MIGRATION_WIZARD_EVENTS.PULL_REQUEST_SELECT);
        setStep("pull-request");
      } else {
        setStep("runners");
      }
    };
    $[26] = isAutoMigrate;
    $[27] = posthog;
    $[28] = setStep;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const goFromRepositoryToNext = t13;
  let t14;
  if ($[30] !== posthog || $[31] !== setStep) {
    t14 = () => {
      posthog?.capture(MIGRATION_WIZARD_EVENTS.REPOSITORY_SELECT);
      setStep("runners");
    };
    $[30] = posthog;
    $[31] = setStep;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  const goFromRepositoryToRunners = t14;
  let t15;
  if ($[33] !== doMigrateRunners || $[34] !== posthog || $[35] !== singleRepoDockerJobs || $[36] !== singleRepoRunnerPaths || $[37] !== supportsStickyDisks) {
    t15 = () => {
      posthog?.capture(MIGRATION_WIZARD_EVENTS.PULL_REQUEST_GENERATE, {
        repository_count: 1
      });
      doMigrateRunners({
        updatedRunnerPaths: singleRepoRunnerPaths,
        dockerCachingJobs: supportsStickyDisks ? singleRepoDockerJobs : undefined
      });
    };
    $[33] = doMigrateRunners;
    $[34] = posthog;
    $[35] = singleRepoDockerJobs;
    $[36] = singleRepoRunnerPaths;
    $[37] = supportsStickyDisks;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const generateSingleRepoPr = t15;
  let t16;
  if ($[39] !== multiRepoPrState || $[40] !== posthog) {
    t16 = repositoryCount => {
      posthog?.capture(MIGRATION_WIZARD_EVENTS.PULL_REQUEST_GENERATE, {
        repository_count: repositoryCount
      });
      multiRepoPrState.startBatch();
    };
    $[39] = multiRepoPrState;
    $[40] = posthog;
    $[41] = t16;
  } else {
    t16 = $[41];
  }
  const generateMultiRepoPrs = t16;
  let t17;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <DialogHeader><DialogTitle>Migration Wizard</DialogTitle><DialogDescription>Migrate all your GitHub Action runners to Blacksmith in a single step</DialogDescription></DialogHeader>;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== isAutoMigrate || $[44] !== selectedRepositories) {
    let t19;
    if ($[46] !== isAutoMigrate) {
      t19 = repo => <SilentErrorBoundary key={`preload-${repo}`}><Suspense fallback={null}><AutoMigrateRepoPreloader repo={repo} seedDockerCaching={isAutoMigrate} /></Suspense></SilentErrorBoundary>;
      $[46] = isAutoMigrate;
      $[47] = t19;
    } else {
      t19 = $[47];
    }
    t18 = selectedRepositories.map(t19);
    $[43] = isAutoMigrate;
    $[44] = selectedRepositories;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[48] !== setStep) {
    t19 = () => setStep("repository");
    $[48] = setStep;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  const t20 = step !== "repository" && selectedRepositories.length > 0;
  let t21;
  if ($[50] !== t20) {
    t21 = <StepCircle step={1} complete={t20} />;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  const t22 = multiRepoMigrationWizardEnabled ? "Repositories" : "Repository";
  let t23;
  if ($[52] !== batchStarted || $[53] !== t19 || $[54] !== t21 || $[55] !== t22) {
    t23 = <AccordionTrigger className="cursor-pointer" onClick={t19} disabled={batchStarted}>{t21}{t22}</AccordionTrigger>;
    $[52] = batchStarted;
    $[53] = t19;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== multiRepoMigrationWizardEnabled || $[58] !== openDropdownId || $[59] !== selectedRepositories) {
    t24 = <RepositoryMultiSelect selectedRepositories={selectedRepositories} setSelectedRepositories={setSelectedRepositories} openDropdownId={openDropdownId} setOpenDropdownId={setOpenDropdownId} allowMultiple={multiRepoMigrationWizardEnabled} />;
    $[57] = multiRepoMigrationWizardEnabled;
    $[58] = openDropdownId;
    $[59] = selectedRepositories;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <div className="text-sm text-left text-neutral-500 mt-1 px-2">Don't see your repository?{" "}<Link href={process.env.NEXT_PUBLIC_GITHUB_APP_INSTALLATION_URL as string} className="text-primary hover:underline rounded-md border border-transparent focus-visible:outline-none focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:ring-0" target="_blank" rel="noopener noreferrer">Adjust permissions here</Link></div>;
    $[61] = t25;
  } else {
    t25 = $[61];
  }
  let t26;
  if ($[62] !== goFromRepositoryToRunners || $[63] !== isAutoMigrate || $[64] !== selectedRepositories.length) {
    t26 = isAutoMigrate && <Button onClick={goFromRepositoryToRunners} variant="secondary" disabled={selectedRepositories.length === 0}>Customize runners</Button>;
    $[62] = goFromRepositoryToRunners;
    $[63] = isAutoMigrate;
    $[64] = selectedRepositories.length;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  const t27 = selectedRepositories.length === 0;
  const t28 = isAutoMigrate ? "Migrate all" : "Next";
  let t29;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <ArrowRight className="w-4 h-4 ml-2" />;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== goFromRepositoryToNext || $[68] !== t27 || $[69] !== t28) {
    t30 = <Button onClick={goFromRepositoryToNext} variant="default" disabled={t27}>{t28}{" "}{t29}</Button>;
    $[67] = goFromRepositoryToNext;
    $[68] = t27;
    $[69] = t28;
    $[70] = t30;
  } else {
    t30 = $[70];
  }
  let t31;
  if ($[71] !== t26 || $[72] !== t30) {
    t31 = <div className="flex items-center justify-end gap-2 mt-4">{t26}{t30}</div>;
    $[71] = t26;
    $[72] = t30;
    $[73] = t31;
  } else {
    t31 = $[73];
  }
  let t32;
  if ($[74] !== t24 || $[75] !== t31) {
    t32 = <AccordionContent className="p-3 space-y-3 text-right">{t24}{t25}{t31}</AccordionContent>;
    $[74] = t24;
    $[75] = t31;
    $[76] = t32;
  } else {
    t32 = $[76];
  }
  let t33;
  if ($[77] !== t23 || $[78] !== t32) {
    t33 = <AccordionItem value="repository">{t23}{t32}</AccordionItem>;
    $[77] = t23;
    $[78] = t32;
    $[79] = t33;
  } else {
    t33 = $[79];
  }
  let t34;
  if ($[80] !== setStep) {
    t34 = () => setStep("runners");
    $[80] = setStep;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  const t35 = selectedRepositories.length === 0 || batchStarted;
  const t36 = (step === "docker-caching" || step === "pull-request") && selectedRepositories.length > 0;
  let t37;
  if ($[82] !== t36) {
    t37 = <StepCircle step={2} complete={t36} />;
    $[82] = t36;
    $[83] = t37;
  } else {
    t37 = $[83];
  }
  let t38;
  if ($[84] !== t34 || $[85] !== t35 || $[86] !== t37) {
    t38 = <AccordionTrigger className="cursor-pointer" onClick={t34} disabled={t35}>{t37}Runners</AccordionTrigger>;
    $[84] = t34;
    $[85] = t35;
    $[86] = t37;
    $[87] = t38;
  } else {
    t38 = $[87];
  }
  let t39;
  if ($[88] !== runnerRepoIndex || $[89] !== selectedRepositories) {
    t39 = <RepoPaginationHeader repositories={selectedRepositories} currentIndex={runnerRepoIndex} />;
    $[88] = runnerRepoIndex;
    $[89] = selectedRepositories;
    $[90] = t39;
  } else {
    t39 = $[90];
  }
  let t40;
  if ($[91] !== currentRunnerRepo || $[92] !== openDropdownId) {
    t40 = currentRunnerRepo && <RunnerStepContent key={currentRunnerRepo} repo={currentRunnerRepo} openDropdownId={openDropdownId} setOpenDropdownId={setOpenDropdownId} />;
    $[91] = currentRunnerRepo;
    $[92] = openDropdownId;
    $[93] = t40;
  } else {
    t40 = $[93];
  }
  let t41;
  if ($[94] !== goFromRunnersToNext || $[95] !== goToRepositoryFromRunners || $[96] !== jobsOnlyLoading || $[97] !== runnerRepoIndex || $[98] !== selectedRepositories) {
    t41 = <RepoPaginationFooter repositories={selectedRepositories} currentIndex={runnerRepoIndex} setCurrentIndex={setRunnerRepoIndex} onBack={goToRepositoryFromRunners} onNext={goFromRunnersToNext} isLoading={jobsOnlyLoading} />;
    $[94] = goFromRunnersToNext;
    $[95] = goToRepositoryFromRunners;
    $[96] = jobsOnlyLoading;
    $[97] = runnerRepoIndex;
    $[98] = selectedRepositories;
    $[99] = t41;
  } else {
    t41 = $[99];
  }
  let t42;
  if ($[100] !== t39 || $[101] !== t40 || $[102] !== t41) {
    t42 = <AccordionContent className="py-0 space-y-0 text-right">{t39}{t40}{t41}</AccordionContent>;
    $[100] = t39;
    $[101] = t40;
    $[102] = t41;
    $[103] = t42;
  } else {
    t42 = $[103];
  }
  let t43;
  if ($[104] !== t38 || $[105] !== t42) {
    t43 = <AccordionItem value="runners">{t38}{t42}</AccordionItem>;
    $[104] = t38;
    $[105] = t42;
    $[106] = t43;
  } else {
    t43 = $[106];
  }
  let t44;
  if ($[107] !== batchStarted || $[108] !== currentDockerRepo || $[109] !== dockerJobsAreLoading || $[110] !== dockerRepoIndex || $[111] !== goFromDockerBack || $[112] !== goFromDockerToNext || $[113] !== isAutoMigrate || $[114] !== selectedRepositories || $[115] !== setStep || $[116] !== step || $[117] !== supportsStickyDisks) {
    t44 = supportsStickyDisks && <AccordionItem value="docker-caching"><AccordionTrigger className="cursor-pointer" onClick={() => setStep("docker-caching")} disabled={selectedRepositories.length === 0 || batchStarted}><StepCircle step={3} complete={step === "pull-request" && selectedRepositories.length > 0} />Docker builds</AccordionTrigger><AccordionContent className="py-0 space-y-0 text-right"><RepoPaginationHeader repositories={selectedRepositories} currentIndex={dockerRepoIndex} />{currentDockerRepo && <DockerStepContent key={currentDockerRepo} repo={currentDockerRepo} autoMigrate={isAutoMigrate} />}<RepoPaginationFooter repositories={selectedRepositories} currentIndex={dockerRepoIndex} setCurrentIndex={setDockerRepoIndex} onBack={goFromDockerBack} onNext={goFromDockerToNext} isLoading={dockerJobsAreLoading} /></AccordionContent></AccordionItem>;
    $[107] = batchStarted;
    $[108] = currentDockerRepo;
    $[109] = dockerJobsAreLoading;
    $[110] = dockerRepoIndex;
    $[111] = goFromDockerBack;
    $[112] = goFromDockerToNext;
    $[113] = isAutoMigrate;
    $[114] = selectedRepositories;
    $[115] = setStep;
    $[116] = step;
    $[117] = supportsStickyDisks;
    $[118] = t44;
  } else {
    t44 = $[118];
  }
  let t45;
  if ($[119] !== setStep) {
    t45 = () => setStep("pull-request");
    $[119] = setStep;
    $[120] = t45;
  } else {
    t45 = $[120];
  }
  const t46 = selectedRepositories.length === 0;
  const t47 = supportsStickyDisks ? 4 : 3;
  let t48;
  if ($[121] !== t47) {
    t48 = <StepCircle step={t47} complete={false} />;
    $[121] = t47;
    $[122] = t48;
  } else {
    t48 = $[122];
  }
  const t49 = isMultiRepo ? "s" : "";
  let t50;
  if ($[123] !== t45 || $[124] !== t46 || $[125] !== t48 || $[126] !== t49) {
    t50 = <AccordionTrigger className="cursor-pointer" onClick={t45} disabled={t46}>{t48}Pull request{t49}</AccordionTrigger>;
    $[123] = t45;
    $[124] = t46;
    $[125] = t48;
    $[126] = t49;
    $[127] = t50;
  } else {
    t50 = $[127];
  }
  let t51;
  if ($[128] !== codesmithEnabled || $[129] !== generateSingleRepoPr || $[130] !== isLoading || $[131] !== isMultiRepo || $[132] !== migrateError || $[133] !== prGenerationLoading || $[134] !== prUrl || $[135] !== resolvedTheme || $[136] !== singleRepo || $[137] !== singleRepoManagedPrId || $[138] !== singleRepoRunnerPaths || $[139] !== singleRepoSessionId) {
    t51 = !isMultiRepo && <SingleRepoPullRequestStep repo={singleRepo} prUrl={prUrl} migrateError={migrateError} isLoading={isLoading} jobsAreLoading={prGenerationLoading} resolvedTheme={resolvedTheme} onGenerate={generateSingleRepoPr} migrationDescription={MIGRATION_PR_SUCCESS_CARD_DESCRIPTION} codesmithEnabled={codesmithEnabled} managedPrId={singleRepoManagedPrId} sessionId={singleRepoSessionId} hasMigratableWork={hasRunnerChanges(singleRepoRunnerPaths)} />;
    $[128] = codesmithEnabled;
    $[129] = generateSingleRepoPr;
    $[130] = isLoading;
    $[131] = isMultiRepo;
    $[132] = migrateError;
    $[133] = prGenerationLoading;
    $[134] = prUrl;
    $[135] = resolvedTheme;
    $[136] = singleRepo;
    $[137] = singleRepoManagedPrId;
    $[138] = singleRepoRunnerPaths;
    $[139] = singleRepoSessionId;
    $[140] = t51;
  } else {
    t51 = $[140];
  }
  let t52;
  if ($[141] !== codesmithEnabled || $[142] !== generateMultiRepoPrs || $[143] !== isMultiRepo || $[144] !== multiRepoPrState.autofixStates || $[145] !== multiRepoPrState.batchStarted || $[146] !== multiRepoPrState.generatingRepos || $[147] !== multiRepoPrState.managedPrIds || $[148] !== multiRepoPrState.onPrError || $[149] !== multiRepoPrState.onPrGenerated || $[150] !== multiRepoPrState.onRepoGenerating || $[151] !== multiRepoPrState.onRepoSkipped || $[152] !== multiRepoPrState.onToggleAllAutofix || $[153] !== multiRepoPrState.prErrors || $[154] !== multiRepoPrState.prUrls || $[155] !== multiRepoPrState.sessionIds || $[156] !== multiRepoPrState.skippedRepos || $[157] !== prGenerationLoading || $[158] !== resolvedTheme || $[159] !== selectedRepositories || $[160] !== supportsStickyDisks) {
    t52 = isMultiRepo && <MultiRepoPullRequestStep repositories={selectedRepositories} supportsStickyDisks={supportsStickyDisks} resolvedTheme={resolvedTheme} dataLoading={prGenerationLoading} prUrls={multiRepoPrState.prUrls} prErrors={multiRepoPrState.prErrors} managedPrIds={multiRepoPrState.managedPrIds} sessionIds={multiRepoPrState.sessionIds} autofixStates={multiRepoPrState.autofixStates} generatingRepos={multiRepoPrState.generatingRepos} skippedRepos={multiRepoPrState.skippedRepos} batchStarted={multiRepoPrState.batchStarted} onPrGenerated={multiRepoPrState.onPrGenerated} onPrError={multiRepoPrState.onPrError} onRepoGenerating={multiRepoPrState.onRepoGenerating} onRepoSkipped={multiRepoPrState.onRepoSkipped} onToggleAllAutofix={multiRepoPrState.onToggleAllAutofix} onGenerateAll={generateMultiRepoPrs} codesmithEnabled={codesmithEnabled} />;
    $[141] = codesmithEnabled;
    $[142] = generateMultiRepoPrs;
    $[143] = isMultiRepo;
    $[144] = multiRepoPrState.autofixStates;
    $[145] = multiRepoPrState.batchStarted;
    $[146] = multiRepoPrState.generatingRepos;
    $[147] = multiRepoPrState.managedPrIds;
    $[148] = multiRepoPrState.onPrError;
    $[149] = multiRepoPrState.onPrGenerated;
    $[150] = multiRepoPrState.onRepoGenerating;
    $[151] = multiRepoPrState.onRepoSkipped;
    $[152] = multiRepoPrState.onToggleAllAutofix;
    $[153] = multiRepoPrState.prErrors;
    $[154] = multiRepoPrState.prUrls;
    $[155] = multiRepoPrState.sessionIds;
    $[156] = multiRepoPrState.skippedRepos;
    $[157] = prGenerationLoading;
    $[158] = resolvedTheme;
    $[159] = selectedRepositories;
    $[160] = supportsStickyDisks;
    $[161] = t52;
  } else {
    t52 = $[161];
  }
  let t53;
  if ($[162] !== t51 || $[163] !== t52) {
    t53 = <AccordionContent className="flex flex-col items-center p-3 py-8 text-center">{t51}{t52}</AccordionContent>;
    $[162] = t51;
    $[163] = t52;
    $[164] = t53;
  } else {
    t53 = $[164];
  }
  let t54;
  if ($[165] !== t50 || $[166] !== t53) {
    t54 = <AccordionItem value="pull-request">{t50}{t53}</AccordionItem>;
    $[165] = t50;
    $[166] = t53;
    $[167] = t54;
  } else {
    t54 = $[167];
  }
  let t55;
  if ($[168] !== step || $[169] !== t33 || $[170] !== t43 || $[171] !== t44 || $[172] !== t54) {
    t55 = <div><Accordion type="single" value={step} collapsible={true} className="mt-3 space-y-3">{t33}{t43}{t44}{t54}</Accordion></div>;
    $[168] = step;
    $[169] = t33;
    $[170] = t43;
    $[171] = t44;
    $[172] = t54;
    $[173] = t55;
  } else {
    t55 = $[173];
  }
  let t56;
  if ($[174] !== t18 || $[175] !== t55) {
    t56 = <>{t17}{t18}{t55}</>;
    $[174] = t18;
    $[175] = t55;
    $[176] = t56;
  } else {
    t56 = $[176];
  }
  return t56;
}
function MigrationPrCard(t0) {
  const $ = _c(15);
  const {
    prUrl,
    repo,
    description,
    autofixEnabled,
    onToggleAutofix
  } = t0;
  let t1;
  if ($[0] !== prUrl) {
    t1 = prUrl.split("/").pop() ?? "0";
    $[0] = prUrl;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const prNumber = parseInt(t1, 10);
  const t2 = `migration-${repo}-${prNumber}`;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Date().toISOString();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== autofixEnabled || $[4] !== description || $[5] !== prNumber || $[6] !== prUrl || $[7] !== repo || $[8] !== t2) {
    t4 = {
      id: t2,
      repository: repo,
      pr_number: prNumber,
      pr_url: prUrl,
      head_branch: "",
      base_branch: "",
      status: "open",
      is_being_autofixed: false,
      autofix_enabled: autofixEnabled,
      created_at: t3,
      title: "Migrate to Blacksmith runners",
      description
    };
    $[3] = autofixEnabled;
    $[4] = description;
    $[5] = prNumber;
    $[6] = prUrl;
    $[7] = repo;
    $[8] = t2;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const pr = t4;
  let t5;
  if ($[10] !== onToggleAutofix) {
    t5 = (_prId, enabled) => onToggleAutofix(enabled);
    $[10] = onToggleAutofix;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== pr || $[13] !== t5) {
    t6 = <div className="w-full max-w-[400px] mt-2 text-left [&>div]:pt-0.5 [&>div]:pb-0.5 [&_.line-clamp-2]:text-xs [&_.line-clamp-2]:leading-4 [&_.pr-card-chat-title]:text-[13px] [&_.pr-card-chat]:gap-0.5 [&_.pr-card-chat]:border [&_.pr-card-chat]:border-[var(--color-border)] [&_.pr-card-chat]:bg-[var(--color-surface)] [&_.pr-card-chat]:py-2.5 [&_.pr-card-chat]:hover:bg-[var(--color-surface-hover)]"><PrCard variant="chat" pr={pr} onToggleAutofix={t5} autofixLabel="Autofix PR" /></div>;
    $[12] = pr;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function SingleRepoPullRequestStep(t0) {
  const $ = _c(17);
  const {
    repo,
    prUrl,
    migrateError,
    isLoading,
    jobsAreLoading,
    resolvedTheme,
    onGenerate,
    migrationDescription,
    codesmithEnabled,
    managedPrId,
    sessionId,
    hasMigratableWork
  } = t0;
  const posthog = usePostHog();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const toggleAutofix = useToggleAutofix(sessionId ?? null);
  const [autofixEnabled, setAutofixEnabled] = useState(true);
  let t1;
  if ($[0] !== activeGithubOrg || $[1] !== autofixEnabled || $[2] !== codesmithEnabled || $[3] !== hasMigratableWork || $[4] !== isLoading || $[5] !== jobsAreLoading || $[6] !== managedPrId || $[7] !== migrateError || $[8] !== migrationDescription || $[9] !== onGenerate || $[10] !== posthog || $[11] !== prUrl || $[12] !== repo || $[13] !== resolvedTheme || $[14] !== sessionId || $[15] !== toggleAutofix) {
    t1 = <><AnimatePresence mode="popLayout" initial={false}>{prUrl ? <motion.div key="single-pr-success" layout={true} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.15,
          ease: "easeOut"
        }}><div className="w-full max-w-[400px] text-left flex flex-col gap-1"><h3 className="type-body-s-medium">Pull request created!</h3><p className="type-body-s-regular text-neutral-500">{codesmithEnabled && managedPrId ? "[code]smith (Blacksmith's cloud coding agent) will automatically address any CI failures on this PR." : "The pull request is ready."}</p>{codesmithEnabled && managedPrId && <MigrationPrCard prUrl={prUrl} repo={repo} description={migrationDescription} autofixEnabled={autofixEnabled} onToggleAutofix={enabled => {
              setAutofixEnabled(enabled);
              toggleAutofix.mutate({
                prId: managedPrId,
                enabled
              }, {
                onError: err => {
                  setAutofixEnabled(!enabled);
                  toast.error(toggleAutofixErrorMessage(err, enabled));
                }
              });
            }} />}</div><div className="flex items-center gap-2 mt-4 w-full max-w-[400px]"><Button variant="default" className="gap-2 flex-1" onClick={() => {
              posthog?.capture(MIGRATION_WIZARD_EVENTS.PULL_REQUEST_VIEW);
              window.open(prUrl, "_blank", "noopener,noreferrer");
            }}><ThemedGitHubIcon theme={resolvedTheme} />View pull request</Button>{codesmithEnabled && activeGithubOrg && sessionId && <Button variant="secondary" className="gap-2 flex-1" onClick={() => {
              window.open(`/${activeGithubOrg}/sessions/${sessionId}`, "_blank", "noopener,noreferrer");
            }}><CodesmithIcon className="size-4" />View in [code]smith</Button>}</div></motion.div> : migrateError && migrateError.response?.status === 403 ? <motion.div key="single-pr-403" layout={true} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.15,
          ease: "easeOut"
        }}><PullRequestPermissionsErrorCard error={migrateError} /></motion.div> : !hasMigratableWork && !isLoading && !jobsAreLoading ? <motion.div key="single-pr-nothing-to-migrate" layout={true} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.15,
          ease: "easeOut"
        }}><h3 className="type-body-l-regular">No changes to migrate</h3><p className="max-w-[400px] mx-auto text-neutral-500 mt-1">Every job is already on its target runner. Go back and pick a Blacksmith runner for at least one job.</p></motion.div> : <motion.div key="single-pr-compose" layout={true} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.15,
          ease: "easeOut"
        }}><h3 className="type-body-s-medium">{isLoading ? "Creating a migration PR" : "Ready to migrate to Blacksmith!"}</h3>{isLoading ? <RotatingLoadingMessage /> : <p className="max-w-[400px] mx-auto text-neutral-500">Blacksmith will create a pull request for review.</p>}<Button variant="default" className="gap-2 mt-4 w-[210px]" onClick={onGenerate} disabled={jobsAreLoading || isLoading || !hasMigratableWork}>{jobsAreLoading || isLoading ? <><Loader2 className="h-4 w-4 animate-spin" />Generating pull request</> : <><ThemedGitHubIcon theme={resolvedTheme} />Generate pull request</>}</Button></motion.div>}</AnimatePresence></>;
    $[0] = activeGithubOrg;
    $[1] = autofixEnabled;
    $[2] = codesmithEnabled;
    $[3] = hasMigratableWork;
    $[4] = isLoading;
    $[5] = jobsAreLoading;
    $[6] = managedPrId;
    $[7] = migrateError;
    $[8] = migrationDescription;
    $[9] = onGenerate;
    $[10] = posthog;
    $[11] = prUrl;
    $[12] = repo;
    $[13] = resolvedTheme;
    $[14] = sessionId;
    $[15] = toggleAutofix;
    $[16] = t1;
  } else {
    t1 = $[16];
  }
  return t1;
}

/**
 * Invisible component that fires a single PR-generation request for its
 * assigned repo once `shouldGenerate` flips true. Skips repos with no
 * runner changes. All callbacks are expected to be stable across renders
 * (which they are when they come from `useMultiRepoPrState`).
 */
function RepoPRTrigger(t0) {
  const $ = _c(30);
  const {
    repo,
    supportsStickyDisks,
    shouldGenerate,
    onGenerating,
    onSkipped,
    onPrGenerated,
    onPrError
  } = t0;
  const [updatedRunnerPaths] = useRunnerPathsForRepo(repo);
  const [dockerCachingJobs] = useDockerCachingJobsForRepo(repo);
  let t1;
  if ($[0] !== repo) {
    t1 = {
      repositoryName: repo
    };
    $[0] = repo;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    doMigrateRunners,
    prUrl,
    managedPrId,
    sessionId,
    migrateError
  } = useMigrateRunnersForRepository(t1);
  const [triggered, setTriggered] = useState(false);
  let t2;
  if ($[2] !== updatedRunnerPaths) {
    t2 = hasRunnerChanges(updatedRunnerPaths);
    $[2] = updatedRunnerPaths;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const hasChanges = t2;
  let t3;
  let t4;
  if ($[4] !== doMigrateRunners || $[5] !== dockerCachingJobs || $[6] !== hasChanges || $[7] !== onGenerating || $[8] !== onSkipped || $[9] !== prUrl || $[10] !== repo || $[11] !== shouldGenerate || $[12] !== supportsStickyDisks || $[13] !== triggered || $[14] !== updatedRunnerPaths) {
    t3 = () => {
      if (!shouldGenerate || triggered || prUrl) {
        return;
      }
      setTriggered(true);
      if (!hasChanges) {
        onSkipped(repo);
        return;
      }
      onGenerating(repo);
      doMigrateRunners({
        updatedRunnerPaths,
        dockerCachingJobs: supportsStickyDisks ? dockerCachingJobs : undefined
      });
    };
    t4 = [shouldGenerate, triggered, prUrl, hasChanges, repo, doMigrateRunners, updatedRunnerPaths, dockerCachingJobs, supportsStickyDisks, onGenerating, onSkipped];
    $[4] = doMigrateRunners;
    $[5] = dockerCachingJobs;
    $[6] = hasChanges;
    $[7] = onGenerating;
    $[8] = onSkipped;
    $[9] = prUrl;
    $[10] = repo;
    $[11] = shouldGenerate;
    $[12] = supportsStickyDisks;
    $[13] = triggered;
    $[14] = updatedRunnerPaths;
    $[15] = t3;
    $[16] = t4;
  } else {
    t3 = $[15];
    t4 = $[16];
  }
  useEffect(t3, t4);
  let t5;
  let t6;
  if ($[17] !== managedPrId || $[18] !== onPrGenerated || $[19] !== prUrl || $[20] !== repo || $[21] !== sessionId) {
    t5 = () => {
      if (prUrl) {
        onPrGenerated(repo, prUrl, managedPrId ?? null, sessionId ?? null);
      }
    };
    t6 = [prUrl, managedPrId, sessionId, repo, onPrGenerated];
    $[17] = managedPrId;
    $[18] = onPrGenerated;
    $[19] = prUrl;
    $[20] = repo;
    $[21] = sessionId;
    $[22] = t5;
    $[23] = t6;
  } else {
    t5 = $[22];
    t6 = $[23];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[24] !== migrateError || $[25] !== onPrError || $[26] !== repo || $[27] !== triggered) {
    t7 = () => {
      if (migrateError && triggered) {
        onPrError(repo, migrateError);
      }
    };
    t8 = [migrateError, triggered, repo, onPrError];
    $[24] = migrateError;
    $[25] = onPrError;
    $[26] = repo;
    $[27] = triggered;
    $[28] = t7;
    $[29] = t8;
  } else {
    t7 = $[28];
    t8 = $[29];
  }
  useEffect(t7, t8);
  return null;
}

/**
 * Invisible component that reads a repo's runner-paths atom and reports
 * upward whether any paths have actually been changed vs. their defaults.
 * Only fires the callback when the boolean value transitions to avoid
 * update loops across repo-scoped atoms.
 */
function RepoChangesChecker(t0) {
  const $ = _c(14);
  const {
    repo,
    onHasChanges,
    resetKey
  } = t0;
  const [updatedRunnerPaths] = useRunnerPathsForRepo(repo);
  let t1;
  if ($[0] !== updatedRunnerPaths) {
    t1 = hasRunnerChanges(updatedRunnerPaths);
    $[0] = updatedRunnerPaths;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasChanges = t1;
  const lastReportedRef = useRef(null);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      lastReportedRef.current = null;
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== resetKey) {
    t3 = [resetKey];
    $[3] = resetKey;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t2, t3);
  let t4;
  if ($[5] !== hasChanges || $[6] !== onHasChanges || $[7] !== repo) {
    t4 = () => {
      if (lastReportedRef.current === hasChanges) {
        return;
      }
      lastReportedRef.current = hasChanges;
      onHasChanges(repo, hasChanges);
    };
    $[5] = hasChanges;
    $[6] = onHasChanges;
    $[7] = repo;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== hasChanges || $[10] !== onHasChanges || $[11] !== repo || $[12] !== resetKey) {
    t5 = [repo, hasChanges, onHasChanges, resetKey];
    $[9] = hasChanges;
    $[10] = onHasChanges;
    $[11] = repo;
    $[12] = resetKey;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  useEffect(t4, t5);
  return null;
}
function MultiRepoPullRequestStep(t0) {
  const $ = _c(80);
  const {
    repositories,
    prUrls,
    prErrors,
    managedPrIds,
    sessionIds,
    autofixStates,
    generatingRepos,
    batchStarted,
    supportsStickyDisks,
    resolvedTheme,
    onGenerateAll,
    onPrGenerated,
    onPrError,
    onRepoGenerating,
    onToggleAllAutofix,
    skippedRepos,
    onRepoSkipped,
    dataLoading,
    codesmithEnabled
  } = t0;
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  let t1;
  if ($[0] !== repositories || $[1] !== skippedRepos) {
    let t2;
    if ($[3] !== skippedRepos) {
      t2 = r => !skippedRepos.has(r);
      $[3] = skippedRepos;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = repositories.filter(t2);
    $[0] = repositories;
    $[1] = skippedRepos;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const activeRepos = t1;
  let t2;
  if ($[5] !== batchStarted || $[6] !== prErrors || $[7] !== prUrls || $[8] !== repositories || $[9] !== skippedRepos) {
    t2 = batchStarted && repositories.every(r_0 => prUrls[r_0] || prErrors[r_0] || skippedRepos.has(r_0));
    $[5] = batchStarted;
    $[6] = prErrors;
    $[7] = prUrls;
    $[8] = repositories;
    $[9] = skippedRepos;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const allDone = t2;
  let t3;
  if ($[11] !== prUrls) {
    t3 = Object.keys(prUrls);
    $[11] = prUrls;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const completedCount = t3.length;
  let t4;
  if ($[13] !== activeRepos || $[14] !== prUrls) {
    let t5;
    if ($[16] !== prUrls) {
      t5 = repo => prUrls[repo];
      $[16] = prUrls;
      $[17] = t5;
    } else {
      t5 = $[17];
    }
    t4 = activeRepos.filter(t5);
    $[13] = activeRepos;
    $[14] = prUrls;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const completedRepos = t4;
  let t5;
  if ($[18] !== autofixStates || $[19] !== completedRepos) {
    t5 = completedRepos.length > 0 && completedRepos.every(repo_0 => autofixStates[repo_0]?.enabled ?? true);
    $[18] = autofixStates;
    $[19] = completedRepos;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const allAutofixEnabled = t5;
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = new Set();
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const [reposWithChanges, setReposWithChanges] = useState(t6);
  let t7;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = new Set();
    $[22] = t7;
  } else {
    t7 = $[22];
  }
  const [checkedRepos, setCheckedRepos] = useState(t7);
  let t8;
  if ($[23] !== repositories) {
    t8 = repositories.join(",");
    $[23] = repositories;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const reposKey = t8;
  const prevReposKeyRef = useRef(reposKey);
  let t10;
  let t9;
  if ($[25] !== reposKey) {
    t9 = () => {
      if (prevReposKeyRef.current === reposKey) {
        return;
      }
      prevReposKeyRef.current = reposKey;
      setCheckedRepos(new Set());
      setReposWithChanges(new Set());
    };
    t10 = [reposKey];
    $[25] = reposKey;
    $[26] = t10;
    $[27] = t9;
  } else {
    t10 = $[26];
    t9 = $[27];
  }
  useLayoutEffect(t9, t10);
  let t11;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = (repo_1, hasChanges) => {
      setCheckedRepos(prev => prev.has(repo_1) ? prev : new Set(prev).add(repo_1));
      setReposWithChanges(prev_0 => {
        const alreadyTracked = prev_0.has(repo_1);
        if (hasChanges === alreadyTracked) {
          return prev_0;
        }
        const next = new Set(prev_0);
        if (hasChanges) {
          next.add(repo_1);
        } else {
          next.delete(repo_1);
        }
        return next;
      });
    };
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const onHasChanges = t11;
  const migrateableCount = reposWithChanges.size;
  const isDataLoading = dataLoading || checkedRepos.size < repositories.length;
  let t12;
  if ($[29] !== reposKey || $[30] !== repositories) {
    let t13;
    if ($[32] !== reposKey) {
      t13 = repo_2 => <RepoChangesChecker key={`check-${repo_2}`} repo={repo_2} onHasChanges={onHasChanges} resetKey={reposKey} />;
      $[32] = reposKey;
      $[33] = t13;
    } else {
      t13 = $[33];
    }
    t12 = repositories.map(t13);
    $[29] = reposKey;
    $[30] = repositories;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[34] !== batchStarted || $[35] !== onPrError || $[36] !== onPrGenerated || $[37] !== onRepoGenerating || $[38] !== onRepoSkipped || $[39] !== repositories || $[40] !== supportsStickyDisks) {
    let t14;
    if ($[42] !== batchStarted || $[43] !== onPrError || $[44] !== onPrGenerated || $[45] !== onRepoGenerating || $[46] !== onRepoSkipped || $[47] !== supportsStickyDisks) {
      t14 = repo_3 => <RepoPRTrigger key={repo_3} repo={repo_3} supportsStickyDisks={supportsStickyDisks} onPrGenerated={onPrGenerated} onPrError={onPrError} onGenerating={onRepoGenerating} onSkipped={onRepoSkipped} shouldGenerate={batchStarted} />;
      $[42] = batchStarted;
      $[43] = onPrError;
      $[44] = onPrGenerated;
      $[45] = onRepoGenerating;
      $[46] = onRepoSkipped;
      $[47] = supportsStickyDisks;
      $[48] = t14;
    } else {
      t14 = $[48];
    }
    t13 = repositories.map(t14);
    $[34] = batchStarted;
    $[35] = onPrError;
    $[36] = onPrGenerated;
    $[37] = onRepoGenerating;
    $[38] = onRepoSkipped;
    $[39] = repositories;
    $[40] = supportsStickyDisks;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[49] !== batchStarted || $[50] !== isDataLoading) {
    t14 = !batchStarted && isDataLoading && <div className="flex flex-col items-center"><LoadingSpinner /><p className="text-neutral-500 mt-3">Preparing PRs...</p></div>;
    $[49] = batchStarted;
    $[50] = isDataLoading;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  let t15;
  if ($[52] !== batchStarted || $[53] !== isDataLoading || $[54] !== migrateableCount || $[55] !== onGenerateAll || $[56] !== resolvedTheme) {
    t15 = !batchStarted && !isDataLoading && <div className="flex flex-col items-center">{migrateableCount > 0 ? <><h3 className="type-body-l-regular">Ready to migrate {migrateableCount}{" "}{migrateableCount === 1 ? "repository" : "repositories"} to Blacksmith!</h3><p className="max-w-[400px] mx-auto text-neutral-500 mt-1">Blacksmith will create a pull request for each repository.</p><Button variant="default" className="gap-2 mt-4 w-[250px]" onClick={() => onGenerateAll(migrateableCount)}><ThemedGitHubIcon theme={resolvedTheme} />Generate pull request{migrateableCount === 1 ? "" : "s"}</Button></> : <><h3 className="type-body-l-regular">No changes to migrate</h3><p className="max-w-[400px] mx-auto text-neutral-500 mt-1">None of the selected repositories have runner changes.</p></>}</div>;
    $[52] = batchStarted;
    $[53] = isDataLoading;
    $[54] = migrateableCount;
    $[55] = onGenerateAll;
    $[56] = resolvedTheme;
    $[57] = t15;
  } else {
    t15 = $[57];
  }
  let t16;
  if ($[58] !== activeGithubOrg || $[59] !== activeRepos || $[60] !== allAutofixEnabled || $[61] !== allDone || $[62] !== api || $[63] !== batchStarted || $[64] !== codesmithEnabled || $[65] !== completedCount || $[66] !== completedRepos || $[67] !== generatingRepos || $[68] !== managedPrIds || $[69] !== onToggleAllAutofix || $[70] !== prErrors || $[71] !== prUrls || $[72] !== sessionIds) {
    t16 = batchStarted && <div className="flex flex-col gap-3 min-w-0">{!allDone && <div className="flex flex-col items-center mb-2"><h3 className="type-body-s-medium">Creating migration PRs ({completedCount}/{activeRepos.length})</h3><RotatingLoadingMessage /></div>}{allDone && <div className="flex flex-col items-center mb-2 w-full min-w-0">{activeRepos.length > 0 ? <><h3 className="type-body-s-medium">Pull requests created!</h3><p className="type-body-s-regular text-neutral-500 mt-0.5 whitespace-nowrap truncate max-w-full">{codesmithEnabled ? `${completedCount} of ${activeRepos.length} pull request${activeRepos.length === 1 ? " is" : "s are"} ready. [code]smith (Blacksmith's cloud coding agent) will automatically address any CI failures on ${activeRepos.length === 1 ? "this PR" : "these PRs"}.` : `${completedCount} of ${activeRepos.length} pull request${activeRepos.length === 1 ? " is" : "s are"} ready.`}</p></> : <><h3 className="type-body-l-regular">No changes to migrate</h3><p className="text-neutral-500 mt-1">None of the selected repositories have runner changes.</p></>}</div>}{activeRepos.length > 0 && <div className="w-full border border-[var(--color-border)] rounded-lg overflow-hidden"><div className="flex items-center justify-between px-4 h-10 border-b border-[var(--color-border)] bg-[var(--color-surface)]"><span className="type-body-s-medium text-neutral-900 dark:text-neutral-100">Pull requests</span>{codesmithEnabled && <AutofixToggle pr={{
            id: "migration-all",
            autofix_enabled: allAutofixEnabled
          }} onToggle={(_id, enabled) => {
            onToggleAllAutofix(completedRepos, enabled);
            const reposWithPrIds = completedRepos.filter(repo_4 => !!managedPrIds[repo_4]);
            const patches = reposWithPrIds.map(repo_5 => api.patch(`/assistant/managed-prs/${managedPrIds[repo_5]}/autofix`, {
              enabled
            }).then(() => repo_5));
            Promise.allSettled(patches).then(results => {
              const failedRepos = reposWithPrIds.filter((_, i) => results[i].status === "rejected");
              if (failedRepos.length > 0) {
                onToggleAllAutofix(failedRepos, !enabled);
                const firstRejection = results.find(_temp4);
                toast.error(toggleAutofixErrorMessage(firstRejection?.reason, enabled, `Failed to toggle autofix for ${failedRepos.length} PR${failedRepos.length > 1 ? "s" : ""}.`));
              }
            });
          }} tabIndex={allDone ? 0 : -1} label="Autofix" disabled={!allDone} />}</div>{activeRepos.map(repo_6 => {
          const url = prUrls[repo_6];
          const error = prErrors[repo_6];
          const generating = generatingRepos.has(repo_6) && !url && !error;
          return <MultiRepoPrResultRow key={repo_6} repo={repo_6} url={url} error={error} generating={generating} codesmithEnabled={codesmithEnabled} org={activeGithubOrg ?? ""} sessionId={sessionIds[repo_6] ?? null} />;
        })}</div>}{allDone && <div className="flex justify-center mt-3"><DialogClose asChild={true}><Button variant="default">Done</Button></DialogClose></div>}</div>;
    $[58] = activeGithubOrg;
    $[59] = activeRepos;
    $[60] = allAutofixEnabled;
    $[61] = allDone;
    $[62] = api;
    $[63] = batchStarted;
    $[64] = codesmithEnabled;
    $[65] = completedCount;
    $[66] = completedRepos;
    $[67] = generatingRepos;
    $[68] = managedPrIds;
    $[69] = onToggleAllAutofix;
    $[70] = prErrors;
    $[71] = prUrls;
    $[72] = sessionIds;
    $[73] = t16;
  } else {
    t16 = $[73];
  }
  let t17;
  if ($[74] !== t12 || $[75] !== t13 || $[76] !== t14 || $[77] !== t15 || $[78] !== t16) {
    t17 = <div className="w-full min-w-0">{t12}{t13}{t14}{t15}{t16}</div>;
    $[74] = t12;
    $[75] = t13;
    $[76] = t14;
    $[77] = t15;
    $[78] = t16;
    $[79] = t17;
  } else {
    t17 = $[79];
  }
  return t17;
}

/**
 * One row in the multi-repo PR results table. Shows the repo name alongside
 * a status icon and either a "View PR" button (success), an error label
 * (failed), or a "Generating..." label (in flight).
 */
function _temp4(result) {
  return result.status === "rejected";
}
function MultiRepoPrResultRow(t0) {
  const $ = _c(29);
  const {
    repo,
    url,
    error,
    generating,
    codesmithEnabled,
    org,
    sessionId
  } = t0;
  const posthog = usePostHog();
  let icon;
  if (url) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <CheckCircleIcon className="w-4 h-4 text-green-500 shrink-0" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    icon = t1;
  } else {
    if (error) {
      let t1;
      if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
        t1 = <ExclamationCircleIcon className="w-4 h-4 text-red-500 shrink-0" />;
        $[1] = t1;
      } else {
        t1 = $[1];
      }
      icon = t1;
    } else {
      if (generating) {
        let t1;
        if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <LoadingSpinner className="w-4 h-4 shrink-0" />;
          $[2] = t1;
        } else {
          t1 = $[2];
        }
        icon = t1;
      } else {
        let t1;
        if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <div className="w-4 h-4 rounded-full bg-neutral-300 dark:bg-neutral-600 shrink-0" />;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        icon = t1;
      }
    }
  }
  let t1;
  if ($[4] !== repo) {
    t1 = <span className="type-body-s-regular truncate">{repo}</span>;
    $[4] = repo;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== icon || $[7] !== t1) {
    t2 = <div className="flex items-center gap-2 min-w-0">{icon}{t1}</div>;
    $[6] = icon;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== posthog || $[10] !== url) {
    t3 = url && <Tooltip delayDuration={700}><TooltipTrigger asChild={true}><Button variant="secondary" size="sm" className="p-0 rounded-full opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity" style={{
          height: 24,
          minHeight: 24,
          maxHeight: 24,
          width: 24,
          minWidth: 24,
          maxWidth: 24
        }} onClick={() => {
          posthog?.capture(MIGRATION_WIZARD_EVENTS.PULL_REQUEST_VIEW);
          window.open(url, "_blank", "noopener,noreferrer");
        }} aria-label="View PR"><Github className="size-3.5 shrink-0" /></Button></TooltipTrigger><TooltipContent className="px-1.5 py-0.5 rounded-[5px]">View PR in GitHub</TooltipContent></Tooltip>;
    $[9] = posthog;
    $[10] = url;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== codesmithEnabled || $[13] !== org || $[14] !== sessionId || $[15] !== url) {
    t4 = url && codesmithEnabled && org && sessionId && <Button variant="secondary" size="sm" className="gap-1.5 text-xs px-2" style={{
      height: 24,
      minHeight: 24,
      maxHeight: 24
    }} onClick={() => {
      window.open(`/${org}/sessions/${sessionId}`, "_blank", "noopener,noreferrer");
    }}><CodesmithIcon className="size-3.5" />View in [code]smith</Button>;
    $[12] = codesmithEnabled;
    $[13] = org;
    $[14] = sessionId;
    $[15] = url;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== error) {
    t5 = error && <span className="text-xs text-red-500">{error.response?.status === 403 ? "No permission" : "Failed"}</span>;
    $[17] = error;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== generating) {
    t6 = generating && <span className="text-xs text-neutral-400">Generating...</span>;
    $[19] = generating;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  let t7;
  if ($[21] !== t3 || $[22] !== t4 || $[23] !== t5 || $[24] !== t6) {
    t7 = <div className="shrink-0 ml-3 flex items-center gap-1">{t3}{t4}{t5}{t6}</div>;
    $[21] = t3;
    $[22] = t4;
    $[23] = t5;
    $[24] = t6;
    $[25] = t7;
  } else {
    t7 = $[25];
  }
  let t8;
  if ($[26] !== t2 || $[27] !== t7) {
    t8 = <div className="group flex items-center justify-between px-4 h-10 border-b border-[var(--color-border)] last:border-b-0 bg-[var(--color-surface)]">{t2}{t7}</div>;
    $[26] = t2;
    $[27] = t7;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  return t8;
}

/**
 * Destructive alert card used across the wizard for error + permission
 * states. Accepts a title and body; when `showPermissionsLink` is true we
 * also render the "Review request" link to the org's installations page.
 */
function WizardErrorCard(t0) {
  const $ = _c(12);
  const {
    title,
    children,
    showPermissionsLink: t1
  } = t0;
  const showPermissionsLink = t1 === undefined ? false : t1;
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const permissionsUrl = `https://github.com/organizations/${activeGithubOrg}/settings/installations`;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <ExclamationCircleIcon className="w-6 h-6" />;
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== title) {
    t3 = <AlertTitle className="my-1.5 font-bold">{title}</AlertTitle>;
    $[1] = title;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== permissionsUrl || $[4] !== showPermissionsLink) {
    t4 = showPermissionsLink && <div className="mt-4">You can do this{" "}<a className="underline text-primary" href={permissionsUrl}>here</a>{" "}by clicking {"\"Review request\""} for our GitHub integration.</div>;
    $[3] = permissionsUrl;
    $[4] = showPermissionsLink;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== children || $[7] !== t4) {
    t5 = <AlertDescription>{children}{t4}</AlertDescription>;
    $[6] = children;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = <div className="p-6"><Alert variant="destructive" className="text-left rounded-md">{t2}{t3}{t5}</Alert></div>;
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function PullRequestPermissionsErrorCard(t0) {
  const $ = _c(1);
  const {
    error
  } = t0;
  if (error.response?.status !== 403) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <WizardErrorCard title="Blacksmith does not have permissions to create pull requests on this repository" showPermissionsLink={true}>Blacksmith requires additional permissions to be able to create pull requests on this repository. Please contact your GitHub organization administrator to approve the necessary permissions.</WizardErrorCard>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  return t1;
}
function RunnerSelectionStepErrorCard(t0) {
  const $ = _c(2);
  const {
    error
  } = t0;
  if (error.response?.status !== 403) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <WizardErrorCard title="Failed to retrieve workflows for this repository">Blacksmith encountered an error while trying to retrieve the workflows for this repository. Please try again.</WizardErrorCard>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <WizardErrorCard title="Blacksmith does not have permissions to access this repository" showPermissionsLink={true}>Blacksmith requires additional permissions to access the contents of this repository. Please contact your GitHub organization administrator to approve the necessary permissions.</WizardErrorCard>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}

// Badge component to display the type of workflow file
function WorkflowTypeBadge(t0) {
  const $ = _c(3);
  const {
    type
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      workflow: "Workflow",
      reusable_workflow: "Reusable"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const labels = t1;
  const t2 = labels[type];
  let t3;
  if ($[1] !== t2) {
    t3 = <span className="text-xs text-neutral-500 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 px-1.5 py-0.5 rounded ml-2">{t2}</span>;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  return t3;
}
function NoRunnersCollapsible(t0) {
  const $ = _c(18);
  const {
    files,
    updateRunnerPath,
    updatedRunnerPaths,
    selectedRepository,
    openDropdownId,
    setOpenDropdownId,
    onWorkflowEmpty
  } = t0;
  const [expanded, setExpanded] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setExpanded(_temp5);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== files.length) {
    t2 = <span>No GitHub runners detected ({files.length})</span>;
    $[1] = files.length;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronsUpDown className="w-3 h-3" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <button type="button" onClick={t1} className="w-full flex items-center justify-between px-4 py-2 text-[10px] uppercase tracking-wider text-neutral-400 bg-neutral-100 dark:bg-neutral-800/50 border-t cursor-pointer hover:bg-neutral-200/60 dark:hover:bg-neutral-700/40 transition-colors">{t2}{t3}</button>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== expanded || $[7] !== files || $[8] !== onWorkflowEmpty || $[9] !== openDropdownId || $[10] !== selectedRepository || $[11] !== setOpenDropdownId || $[12] !== updateRunnerPath || $[13] !== updatedRunnerPaths) {
    t5 = expanded && files.map(file => <WorkflowItem key={file.path} workflow={file} updateRunnerPath={updateRunnerPath} updatedRunnerPaths={updatedRunnerPaths} selectedRepository={selectedRepository} openDropdownId={openDropdownId} setOpenDropdownId={setOpenDropdownId} onEmpty={onWorkflowEmpty} />);
    $[6] = expanded;
    $[7] = files;
    $[8] = onWorkflowEmpty;
    $[9] = openDropdownId;
    $[10] = selectedRepository;
    $[11] = setOpenDropdownId;
    $[12] = updateRunnerPath;
    $[13] = updatedRunnerPaths;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <div>{t4}{t5}</div>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function _temp5(prev) {
  return !prev;
}
function RunnerSelectionStep(t0) {
  const $ = _c(43);
  const {
    selectedRepository,
    updateRunnerPath,
    updatedRunnerPaths,
    openDropdownId,
    setOpenDropdownId
  } = t0;
  const {
    workflows,
    reusableWorkflows,
    error
  } = useRunsOnFilesForRepository(selectedRepository);
  let t1;
  if ($[0] !== reusableWorkflows || $[1] !== workflows) {
    t1 = [...workflows, ...reusableWorkflows];
    $[0] = reusableWorkflows;
    $[1] = workflows;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const allFiles = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Set();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [emptyWorkflows, setEmptyWorkflows] = useState(t2);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = path => {
      setEmptyWorkflows(prev => {
        if (prev.has(path)) {
          return prev;
        }
        const next = new Set(prev);
        next.add(path);
        return next;
      });
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const onWorkflowEmpty = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = ["jobs"];
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const jobsFetchCount = useIsFetching(t4);
  const jobsLoading = jobsFetchCount > 0;
  let t5;
  bb0: {
    if (jobsLoading) {
      let t6;
      if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = [];
        $[6] = t6;
      } else {
        t6 = $[6];
      }
      let t7;
      if ($[7] !== allFiles) {
        t7 = {
          withJobs: allFiles,
          withoutJobs: t6 as WorkflowFileData[]
        };
        $[7] = allFiles;
        $[8] = t7;
      } else {
        t7 = $[8];
      }
      t5 = t7;
      break bb0;
    }
    let t6;
    if ($[9] !== allFiles || $[10] !== emptyWorkflows) {
      t6 = {
        withJobs: allFiles.filter(f => !emptyWorkflows.has(f.path)),
        withoutJobs: allFiles.filter(f_0 => emptyWorkflows.has(f_0.path))
      };
      $[9] = allFiles;
      $[10] = emptyWorkflows;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    t5 = t6;
  }
  const sortedFiles = t5;
  let t6;
  if ($[12] !== allFiles) {
    t6 = allFiles?.[0] ? [allFiles[0].path] : [];
    $[12] = allFiles;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== error) {
    t7 = error && <RunnerSelectionStepErrorCard error={error} />;
    $[14] = error;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== error || $[17] !== openDropdownId || $[18] !== selectedRepository || $[19] !== setOpenDropdownId || $[20] !== sortedFiles || $[21] !== updateRunnerPath || $[22] !== updatedRunnerPaths) {
    t8 = !error && sortedFiles.withJobs.map(file => <WorkflowItem key={file.path} workflow={file} updateRunnerPath={updateRunnerPath} updatedRunnerPaths={updatedRunnerPaths} selectedRepository={selectedRepository} openDropdownId={openDropdownId} setOpenDropdownId={setOpenDropdownId} onEmpty={onWorkflowEmpty} />);
    $[16] = error;
    $[17] = openDropdownId;
    $[18] = selectedRepository;
    $[19] = setOpenDropdownId;
    $[20] = sortedFiles;
    $[21] = updateRunnerPath;
    $[22] = updatedRunnerPaths;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== error || $[25] !== openDropdownId || $[26] !== selectedRepository || $[27] !== setOpenDropdownId || $[28] !== sortedFiles || $[29] !== updateRunnerPath || $[30] !== updatedRunnerPaths) {
    t9 = !error && sortedFiles.withoutJobs.length > 0 && <NoRunnersCollapsible files={sortedFiles.withoutJobs} updateRunnerPath={updateRunnerPath} updatedRunnerPaths={updatedRunnerPaths} selectedRepository={selectedRepository} openDropdownId={openDropdownId} setOpenDropdownId={setOpenDropdownId} onWorkflowEmpty={onWorkflowEmpty} />;
    $[24] = error;
    $[25] = openDropdownId;
    $[26] = selectedRepository;
    $[27] = setOpenDropdownId;
    $[28] = sortedFiles;
    $[29] = updateRunnerPath;
    $[30] = updatedRunnerPaths;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  let t10;
  if ($[32] !== allFiles?.length || $[33] !== error) {
    t10 = !error && allFiles?.length === 0 && <div className="space-y-1 p-1"><div className="flex items-center justify-center h-[280px] w-full text-neutral-500">No workflows found in this repository</div></div>;
    $[32] = allFiles?.length;
    $[33] = error;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  let t11;
  if ($[35] !== t10 || $[36] !== t7 || $[37] !== t8 || $[38] !== t9) {
    t11 = <ScrollArea className="h-[280px] border-b w-full">{t7}{t8}{t9}{t10}</ScrollArea>;
    $[35] = t10;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
    $[39] = t11;
  } else {
    t11 = $[39];
  }
  let t12;
  if ($[40] !== t11 || $[41] !== t6) {
    t12 = <WorkflowAccordion type="multiple" defaultValue={t6}>{t11}</WorkflowAccordion>;
    $[40] = t11;
    $[41] = t6;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  return t12;
}

/**
 * A single selected-repo pill shown inside the combobox trigger. The remove
 * button is a span with role="button" (not <button>) because the pill lives
 * inside the combobox <button>, and nested native buttons are invalid HTML.
 */
function SelectedRepoPill(t0) {
  const $ = _c(12);
  const {
    repo,
    onRemove
  } = t0;
  const t1 = `Remove ${repo}`;
  let t2;
  let t3;
  if ($[0] !== onRemove || $[1] !== repo) {
    t2 = e => {
      e.stopPropagation();
      onRemove(repo);
    };
    t3 = e_0 => {
      if (e_0.key === "Enter" || e_0.key === " ") {
        e_0.preventDefault();
        e_0.stopPropagation();
        onRemove(repo);
      }
    };
    $[0] = onRemove;
    $[1] = repo;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <X className="w-3 h-3" />;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t1 || $[6] !== t2 || $[7] !== t3) {
    t5 = <span role="button" tabIndex={0} className="hover:opacity-70 cursor-pointer" aria-label={t1} onClick={t2} onKeyDown={t3}>{t4}</span>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== repo || $[10] !== t5) {
    t6 = <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-black/[0.06] text-neutral-800 dark:bg-white/[0.08] dark:text-neutral-200 shrink-0">{repo}{t5}</span>;
    $[9] = repo;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}

/** Small square checkbox used inside CommandItems in the repo dropdown. */
function Checkbox(t0) {
  const $ = _c(7);
  const {
    checked
  } = t0;
  const t1 = checked ? "bg-neutral-900 border-neutral-900 dark:bg-neutral-100 dark:border-neutral-100" : "border-neutral-400";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("mx-2 mt-0.5 h-4 w-4 rounded-sm border flex items-center justify-center", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== checked) {
    t3 = checked && <Check className="h-3 w-3 text-white dark:text-black" />;
    $[2] = checked;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
function RepositoryMultiSelect({
  selectedRepositories,
  setSelectedRepositories,
  openDropdownId,
  setOpenDropdownId,
  allowMultiple = true
}: {
  selectedRepositories: string[];
  setSelectedRepositories: (repos: string[]) => void;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  allowMultiple?: boolean;
}) {
  const {
    resolvedTheme: theme
  } = useTheme();
  const api = useApi();
  const {
    activeGithubOrg
  } = useActiveGithubOrgForUser();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounceValue(search, 300);
  const open = openDropdownId === "repository";
  const setOpen = (next: boolean) => {
    setOpenDropdownId(next ? "repository" : null);
    if (!next) setSearch("");
  };

  // Fetch repos from the server, keyed on the debounced search string.
  // Empty string fetches the initial page; non-empty uses GitHub search.
  // Shared with the env builder wizard via `useRepositorySearch`.
  const {
    data: searchResult,
    isLoading: isSearching
  } = useRepositorySearch({
    endpoint: `user/github/orgs/${activeGithubOrg}/migrate-runners/repositories`,
    search: debouncedSearch,
    perPage: 100,
    enabled: !!activeGithubOrg,
    queryKey: ["repositorySearch", activeGithubOrg, debouncedSearch, 100],
    options: {
      useErrorBoundary: error => (error as AxiosError)?.response?.status !== 404
    }
  });
  const visibleRepos = useMemo(() => {
    if (!searchResult?.data) return [];
    return [...searchResult.data].sort((a, b) => a.name.localeCompare(b.name));
  }, [searchResult?.data]);
  const hasMoreRepos = searchResult?.has_more ?? false;
  const isSelected = (name: string) => selectedRepositories.includes(name);
  const atCap = allowMultiple && selectedRepositories.length >= MAX_MIGRATE_REPOS;
  // "All" means everything that could be selected under the cap.
  const effectiveAllCount = allowMultiple ? Math.min(visibleRepos.length, MAX_MIGRATE_REPOS) : visibleRepos.length;
  const allSelected = effectiveAllCount > 0 && selectedRepositories.length === effectiveAllCount;
  const toggleRepo = (repoName: string) => {
    if (!allowMultiple) {
      setSelectedRepositories(isSelected(repoName) ? [] : [repoName]);
      setOpen(false);
      return;
    }
    if (isSelected(repoName)) {
      setSelectedRepositories(selectedRepositories.filter(r => r !== repoName));
      return;
    }
    if (selectedRepositories.length >= MAX_MIGRATE_REPOS) return;
    setSelectedRepositories([...selectedRepositories, repoName]);
  };
  const toggleAll = () => {
    if (allSelected) {
      setSelectedRepositories([]);
      return;
    }
    setSelectedRepositories(visibleRepos.slice(0, MAX_MIGRATE_REPOS).map(r_0 => r_0.name));
  };
  const removeRepo = (repoName_0: string) => {
    setSelectedRepositories(selectedRepositories.filter(r_1 => r_1 !== repoName_0));
  };
  const placeholder = allowMultiple ? "Select repositories to migrate..." : "Select repository to migrate...";
  const visiblePills = selectedRepositories.slice(0, 5);
  const hiddenPillCount = Math.max(0, selectedRepositories.length - 5);

  // Show loading only on initial load, not while typing (keepPreviousData handles that)
  const isLoadingRepos = isSearching && !searchResult;
  return <div>
      {/* modal so Radix gives the portaled popover its own scroll lock; without
          it the dialog's react-remove-scroll blocks wheel/touch scrolling over
          the repo list because the portaled content lives outside the dialog. */}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button variant="secondary" role="combobox" aria-expanded={open} className={cn("w-full justify-between hover:bg-neutral-100 dark:hover:bg-neutral-800 !border-2", allowMultiple && "h-auto min-h-[40px] py-1.5")}>
            {allowMultiple ? <div className="flex items-center gap-1.5 flex-1 min-w-0 overflow-hidden">
                {selectedRepositories.length === 0 ? <span className="text-neutral-500">{placeholder}</span> : <>
                    {visiblePills.map(repo => <SelectedRepoPill key={repo} repo={repo} onRemove={removeRepo} />)}
                    {hiddenPillCount > 0 && <span className="text-xs text-neutral-500 dark:text-neutral-400 shrink-0">
                        +{hiddenPillCount} more
                      </span>}
                  </>}
              </div> : selectedRepositories.length > 0 ? selectedRepositories[0] : <span className="text-neutral-500">{placeholder}</span>}
            <ChevronsUpDown className="w-4 h-4 ml-2 opacity-50 shrink-0" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[664px] p-0 -mt-2">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search repositories..." value={search} onValueChange={setSearch} />
            {allowMultiple && visibleRepos.length > MAX_MIGRATE_REPOS && <div className="px-3 py-2 border-b type-body-xs-regular text-neutral-500 dark:text-neutral-400">
                {atCap ? `Maximum of ${MAX_MIGRATE_REPOS} repositories reached — deselect one to add another.` : `${selectedRepositories.length} of ${MAX_MIGRATE_REPOS} selected`}
              </div>}
            {isLoadingRepos ? <div className="py-6 text-center type-body-s-regular text-muted-foreground">
                Loading repositories...
              </div> : visibleRepos.length === 0 ? <CommandEmpty>No repository found.</CommandEmpty> : <CommandGroup className="p-0">
                <CommandList>
                  {allowMultiple && !search && <CommandItem value="__select_all__" onSelect={toggleAll} className="items-start py-3 border-b rounded-none">
                      <Checkbox checked={allSelected} />
                      <div className="flex gap-2 type-body-s-regular">
                        {visibleRepos.length > MAX_MIGRATE_REPOS ? `Select first ${MAX_MIGRATE_REPOS}` : "Select all"}
                      </div>
                    </CommandItem>}
                  {visibleRepos.map(repo_0 => {
                const selected = isSelected(repo_0.name);
                const disabled = allowMultiple && !selected && atCap;
                return <CommandItem key={repo_0.name} value={repo_0.name} onSelect={() => toggleRepo(repo_0.name)} disabled={disabled} className="items-start py-3 border-b rounded-none last:border-0">
                        {allowMultiple ? <Checkbox checked={selected} /> : <Check className={cn("mx-2 mt-0.5 h-4 w-4", selected ? "opacity-100" : "opacity-0")} />}
                        <div className="flex items-center gap-2">
                          <span className="shrink-0 w-5 h-5">
                            <ThemedGitHubIconLight theme={theme} />
                          </span>
                          {repo_0.name}
                        </div>
                      </CommandItem>;
              })}
                  {hasMoreRepos && <p className="py-2 text-center type-body-s-regular text-muted-foreground">
                      Type to search for more repositories...
                    </p>}
                </CommandList>
              </CommandGroup>}
          </Command>
        </PopoverContent>
      </Popover>
    </div>;
}
function JobItem(t0) {
  const $ = _c(55);
  const {
    jobName,
    workflow,
    pairIndex,
    currentRunner,
    migratedRunner,
    updateRunnerPath,
    openDropdownId,
    setOpenDropdownId
  } = t0;
  let t1;
  bb0: {
    const isMacOSJob = currentRunner.toLowerCase().includes("macos") || migratedRunner.toLowerCase().includes("macos");
    if (isMacOSJob) {
      t1 = RUNNER_OPTIONS;
      break bb0;
    }
    let t2;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = RUNNER_OPTIONS.filter(_temp9);
      $[0] = t2;
    } else {
      t2 = $[0];
    }
    t1 = t2;
  }
  const filteredRunnerOptions = t1;
  const hasMigrated = currentRunner !== migratedRunner;
  let t2;
  if ($[1] !== migratedRunner) {
    t2 = migratedRunner.toLowerCase().includes("macos");
    $[1] = migratedRunner;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isMacRunner = t2;
  let t3;
  bb1: {
    const source = currentRunner.toLowerCase();
    const target = migratedRunner.toLowerCase();
    if (!target.includes("macos")) {
      t3 = false;
      break bb1;
    }
    if (source.includes("-intel")) {
      t3 = true;
      break bb1;
    }
    if (source.includes("-large") && !source.includes("-xlarge")) {
      t3 = true;
      break bb1;
    }
    if (source.includes("macos-12")) {
      t3 = true;
      break bb1;
    }
    if (source.includes("macos-13") && !source.includes("-xlarge")) {
      t3 = true;
      break bb1;
    }
    t3 = false;
  }
  const isIntelMacOSMigration = t3;
  const dropdownId = `${workflow}-${jobName}-${pairIndex}`;
  const isOpen = openDropdownId === dropdownId;
  const t4 = hasMigrated ? "text-green-500" : "text-neutral-400";
  let t5;
  if ($[3] !== t4) {
    t5 = cn(t4, "w-5 h-5 min-w-[20px]");
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5) {
    t6 = <CheckCircleIcon className={t5} />;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== jobName) {
    t7 = <div className="text-sm truncate max-w-[200px]">{jobName}</div>;
    $[7] = jobName;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t6 || $[10] !== t7) {
    t8 = <div className="flex items-center min-w-0 gap-2">{t6}{t7}</div>;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== hasMigrated || $[13] !== isMacRunner) {
    t9 = isMacRunner && hasMigrated && <Badge variant="muted" className="border-neutral-200 dark:border-neutral-700 leading-none shrink-0">Beta</Badge>;
    $[12] = hasMigrated;
    $[13] = isMacRunner;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== currentRunner) {
    t10 = <span className="font-mono text-xs text-neutral-400">{currentRunner}</span>;
    $[15] = currentRunner;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <MoveRight className="w-4 h-4 text-neutral-400" />;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== dropdownId || $[19] !== setOpenDropdownId) {
    t12 = open => {
      setOpenDropdownId(open ? dropdownId : null);
    };
    $[18] = dropdownId;
    $[19] = setOpenDropdownId;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  let t13;
  if ($[21] !== jobName || $[22] !== pairIndex || $[23] !== setOpenDropdownId || $[24] !== updateRunnerPath || $[25] !== workflow) {
    t13 = runner => {
      updateRunnerPath(workflow, jobName, pairIndex, runner);
      setOpenDropdownId(null);
    };
    $[21] = jobName;
    $[22] = pairIndex;
    $[23] = setOpenDropdownId;
    $[24] = updateRunnerPath;
    $[25] = workflow;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <SelectTrigger className="w-[280px] font-mono [&>span]:min-w-0 [&>span]:truncate"><SelectValue placeholder="select runner type" /></SelectTrigger>;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== currentRunner) {
    t15 = <SelectItem key={currentRunner} value={currentRunner} className="font-mono text-xs">{currentRunner} (do not migrate)</SelectItem>;
    $[28] = currentRunner;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== filteredRunnerOptions) {
    t16 = filteredRunnerOptions.map(_temp10);
    $[30] = filteredRunnerOptions;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t15 || $[33] !== t16) {
    t17 = <SelectContent>{t15}{t16}</SelectContent>;
    $[32] = t15;
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== isOpen || $[36] !== migratedRunner || $[37] !== t12 || $[38] !== t13 || $[39] !== t17) {
    t18 = <Select open={isOpen} onOpenChange={t12} onValueChange={t13} defaultValue={migratedRunner}>{t14}{t17}</Select>;
    $[35] = isOpen;
    $[36] = migratedRunner;
    $[37] = t12;
    $[38] = t13;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== t10 || $[42] !== t18 || $[43] !== t9) {
    t19 = <div className="flex items-center justify-end flex-1 gap-2 min-w-fit">{t9}{t10}{t11}{t18}</div>;
    $[41] = t10;
    $[42] = t18;
    $[43] = t9;
    $[44] = t19;
  } else {
    t19 = $[44];
  }
  let t20;
  if ($[45] !== t19 || $[46] !== t8) {
    t20 = <div className="flex items-center gap-3 px-4 py-1 bg-neutral-50 dark:bg-neutral-900">{t8}{t19}</div>;
    $[45] = t19;
    $[46] = t8;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== hasMigrated || $[49] !== isIntelMacOSMigration) {
    t21 = isIntelMacOSMigration && hasMigrated && <div className="flex items-start gap-1.5 px-4 py-1.5 text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-950/30"><ExclamationCircleIcon className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>Blacksmith macOS runners use Apple Silicon (ARM). Workflows targeting x86_64 may need adjustment.</span></div>;
    $[48] = hasMigrated;
    $[49] = isIntelMacOSMigration;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== jobName || $[52] !== t20 || $[53] !== t21) {
    t22 = <div key={jobName} className="border-b last:border-0">{t20}{t21}</div>;
    $[51] = jobName;
    $[52] = t20;
    $[53] = t21;
    $[54] = t22;
  } else {
    t22 = $[54];
  }
  return t22;
}
function _temp10(runner_0) {
  return <SelectItem key={runner_0} value={runner_0} className="font-mono text-xs">{runner_0}</SelectItem>;
}
function _temp9(opt) {
  return !opt.includes("macos");
}
function WorkflowItem(t0) {
  const $ = _c(52);
  const {
    workflow,
    updatedRunnerPaths,
    updateRunnerPath,
    selectedRepository,
    openDropdownId,
    setOpenDropdownId,
    onEmpty
  } = t0;
  let t1;
  if ($[0] !== selectedRepository || $[1] !== workflow.path) {
    t1 = {
      workflowPath: workflow.path,
      repositoryName: selectedRepository
    };
    $[0] = selectedRepository;
    $[1] = workflow.path;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    jobs,
    isLoading,
    error
  } = useJobsForWorkflow(t1);
  const workflowKey = workflow.path;
  let t2;
  let t3;
  if ($[3] !== error || $[4] !== isLoading || $[5] !== jobs || $[6] !== onEmpty || $[7] !== workflow.path) {
    t2 = () => {
      if (!isLoading && !error && Object.keys(jobs).length === 0) {
        onEmpty?.(workflow.path);
      }
    };
    t3 = [isLoading, error, jobs, workflow.path, onEmpty];
    $[3] = error;
    $[4] = isLoading;
    $[5] = jobs;
    $[6] = onEmpty;
    $[7] = workflow.path;
    $[8] = t2;
    $[9] = t3;
  } else {
    t2 = $[8];
    t3 = $[9];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[10] !== error || $[11] !== isLoading || $[12] !== jobs || $[13] !== updateRunnerPath || $[14] !== updatedRunnerPaths || $[15] !== workflowKey) {
    t4 = () => {
      if (!isLoading && !error) {
        Object.entries(jobs).forEach(t6 => {
          const [jobName, runners] = t6;
          if (updatedRunnerPaths[workflowKey]?.[jobName]) {
            return;
          }
          if (Array.isArray(runners)) {
            toRunnerPairs(runners).forEach((t7, pairIndex) => {
              const [current, recommended] = t7;
              updateRunnerPath(workflowKey, jobName, pairIndex, recommended, current);
            });
          }
        });
      }
    };
    t5 = [jobs, isLoading, error, updateRunnerPath, workflowKey, updatedRunnerPaths];
    $[10] = error;
    $[11] = isLoading;
    $[12] = jobs;
    $[13] = updateRunnerPath;
    $[14] = updatedRunnerPaths;
    $[15] = workflowKey;
    $[16] = t4;
    $[17] = t5;
  } else {
    t4 = $[16];
    t5 = $[17];
  }
  useEffect(t4, t5);
  let t6;
  if ($[18] !== workflow.name) {
    t6 = <span className="px-2 py-1 text-sm font-medium">{workflow.name}</span>;
    $[18] = workflow.name;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  let t7;
  if ($[20] !== workflow.type) {
    t7 = <WorkflowTypeBadge type={workflow.type} />;
    $[20] = workflow.type;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t6 || $[23] !== t7) {
    t8 = <WorkflowAccordionTrigger className="cursor-pointer"><div className="flex items-center">{t6}{t7}</div></WorkflowAccordionTrigger>;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== isLoading) {
    t9 = isLoading && <div className="space-y-1 p-1"><Skeleton className="w-full h-10" /><Skeleton className="w-full h-10" /><Skeleton className="w-full h-10" /></div>;
    $[25] = isLoading;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  let t10;
  if ($[27] !== error || $[28] !== isLoading) {
    t10 = !isLoading && error && <div className="space-y-1 p-1"><div className="flex items-center justify-center h-10 w-full text-neutral-500">An error occurred trying to fetch the jobs for this workflow.</div></div>;
    $[27] = error;
    $[28] = isLoading;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== error || $[31] !== isLoading || $[32] !== jobs) {
    t11 = !isLoading && !error && Object.keys(jobs).length === 0 && <div className="space-y-1 p-1"><div className="flex items-center justify-center h-10 w-full text-neutral-500">No GitHub runners detected</div></div>;
    $[30] = error;
    $[31] = isLoading;
    $[32] = jobs;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== error || $[35] !== isLoading || $[36] !== jobs || $[37] !== openDropdownId || $[38] !== setOpenDropdownId || $[39] !== updateRunnerPath || $[40] !== updatedRunnerPaths || $[41] !== workflowKey) {
    t12 = !isLoading && !error && Object.entries(jobs).flatMap(t13 => {
      const [jobName_0, runners_0] = t13;
      if (!Array.isArray(runners_0)) {
        return [];
      }
      return toRunnerPairs(runners_0).map((t14, pairIndex_0) => {
        const [currentRunner, recommendedRunner] = t14;
        return <JobItem key={`${workflowKey}-${jobName_0}-${pairIndex_0}`} workflow={workflowKey} jobName={jobName_0} pairIndex={pairIndex_0} currentRunner={currentRunner} migratedRunner={updatedRunnerPaths[workflowKey]?.[jobName_0]?.[pairIndex_0 * 2 + 1] ?? recommendedRunner} updateRunnerPath={updateRunnerPath} openDropdownId={openDropdownId} setOpenDropdownId={setOpenDropdownId} />;
      });
    });
    $[34] = error;
    $[35] = isLoading;
    $[36] = jobs;
    $[37] = openDropdownId;
    $[38] = setOpenDropdownId;
    $[39] = updateRunnerPath;
    $[40] = updatedRunnerPaths;
    $[41] = workflowKey;
    $[42] = t12;
  } else {
    t12 = $[42];
  }
  let t13;
  if ($[43] !== t10 || $[44] !== t11 || $[45] !== t12 || $[46] !== t9) {
    t13 = <WorkflowAccordionContent className="flex flex-col">{t9}{t10}{t11}{t12}</WorkflowAccordionContent>;
    $[43] = t10;
    $[44] = t11;
    $[45] = t12;
    $[46] = t9;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== t13 || $[49] !== t8 || $[50] !== workflow.path) {
    t14 = <WorkflowAccordionItem value={workflow.path} key={workflow.path} className="bg-neutral-200/50 dark:bg-neutral-950/40">{t8}{t13}</WorkflowAccordionItem>;
    $[48] = t13;
    $[49] = t8;
    $[50] = workflow.path;
    $[51] = t14;
  } else {
    t14 = $[51];
  }
  return t14;
}

// Component to fetch Docker jobs for a workflow
function DockerJobsFetcher(t0) {
  const $ = _c(16);
  const {
    workflowPath,
    repository,
    plannedMigrations,
    onDataLoaded,
    onLoadingChange
  } = t0;
  let t1;
  if ($[0] !== plannedMigrations || $[1] !== repository || $[2] !== workflowPath) {
    t1 = {
      workflowPath,
      repositoryName: repository,
      plannedMigrations
    };
    $[0] = plannedMigrations;
    $[1] = repository;
    $[2] = workflowPath;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    jobs,
    isLoading,
    error
  } = useDockerJobsForWorkflow(t1);
  let t2;
  let t3;
  if ($[4] !== isLoading || $[5] !== onLoadingChange || $[6] !== workflowPath) {
    t2 = () => {
      onLoadingChange(workflowPath, isLoading);
    };
    t3 = [workflowPath, isLoading, onLoadingChange];
    $[4] = isLoading;
    $[5] = onLoadingChange;
    $[6] = workflowPath;
    $[7] = t2;
    $[8] = t3;
  } else {
    t2 = $[7];
    t3 = $[8];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[9] !== error || $[10] !== isLoading || $[11] !== jobs || $[12] !== onDataLoaded || $[13] !== workflowPath) {
    t4 = () => {
      if (!isLoading && !error && jobs) {
        const dockerJobs = {};
        Object.entries(jobs).forEach(t6 => {
          const [jobName, jobData] = t6;
          if (jobData.dockerBuild?.detected) {
            dockerJobs[jobName] = jobData;
          }
        });
        onDataLoaded(workflowPath, dockerJobs);
      }
    };
    t5 = [jobs, isLoading, error, workflowPath, onDataLoaded];
    $[9] = error;
    $[10] = isLoading;
    $[11] = jobs;
    $[12] = onDataLoaded;
    $[13] = workflowPath;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  useEffect(t4, t5);
  return null;
}

/** A single `-` / `+` diff row in the docker explainer below. */
function DiffLine(t0) {
  const $ = _c(15);
  const {
    sign,
    actionName
  } = t0;
  const bg = sign === "-" ? "bg-red-950/30" : "bg-green-950/30";
  const signColor = sign === "-" ? "text-red-500" : "text-green-500";
  let t1;
  if ($[0] !== bg) {
    t1 = cn("flex px-3 py-1", bg);
    $[0] = bg;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== signColor) {
    t2 = cn("select-none mr-3", signColor);
    $[2] = signColor;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== sign || $[5] !== t2) {
    t3 = <span className={t2}>{sign}</span>;
    $[4] = sign;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="text-blue-400">uses</span>;
    t5 = <span className="text-neutral-400">:</span>;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  let t6;
  if ($[9] !== actionName) {
    t6 = <span className="text-neutral-300">{t4}{t5}{" "}<span className="text-yellow-500">{actionName}</span></span>;
    $[9] = actionName;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t1 || $[12] !== t3 || $[13] !== t6) {
    t7 = <div className={t1}>{t3}{t6}</div>;
    $[11] = t1;
    $[12] = t3;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}
function DiffBlock(t0) {
  const $ = _c(12);
  const {
    label,
    before,
    after
  } = t0;
  let t1;
  if ($[0] !== label) {
    t1 = <div className="text-xs text-neutral-500 mb-2 uppercase tracking-wider">{label}</div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== before) {
    t2 = <DiffLine sign="-" actionName={before} />;
    $[2] = before;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== after) {
    t3 = <DiffLine sign="+" actionName={after} />;
    $[4] = after;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <div className="bg-neutral-900 dark:bg-neutral-950 rounded-lg overflow-hidden border border-[var(--color-border)]"><div className="font-mono text-xs">{t2}{t3}</div></div>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t1 || $[10] !== t4) {
    t5 = <div className="mb-3 last:mb-0">{t1}{t4}</div>;
    $[9] = t1;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}

/** Explains the docker-action swap-out that the wizard will perform. */
function DockerCachingDiffExplainer() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="p-3 border-b"><p className="text-xs text-neutral-500 text-left mb-3">Leverage Blacksmith's Docker layer caching to speed up Docker builds by 40x</p><DiffBlock label="Docker Setup Buildx" before="docker/setup-buildx-action@v2" after="useblacksmith/setup-docker-builder@v1" /><DiffBlock label="Docker Build & Push" before="docker/build-push-action@v4" after="useblacksmith/build-push-action@v2" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function DockerCachingStep({
  selectedRepository,
  dockerCachingJobs,
  setDockerCachingJobs,
  updatedRunnerPaths,
  defaultEnabled = false
}: {
  selectedRepository: string;
  dockerCachingJobs: Record<string, Record<string, boolean>>;
  setDockerCachingJobs: (fn: (draft: Record<string, Record<string, boolean>>) => void) => void;
  updatedRunnerPaths: RunnerPaths;
  defaultEnabled?: boolean;
}) {
  // Same files endpoint as RunnerSelectionStep so docker-jobs keys align.
  const {
    workflows,
    reusableWorkflows,
    error
  } = useRunsOnFilesForRepository(selectedRepository);
  const allFiles = useMemo(() => [...workflows, ...reusableWorkflows], [workflows, reusableWorkflows]);
  const [globalToggle, setGlobalToggle] = useState(defaultEnabled);
  const [workflowDockerData, setWorkflowDockerData] = useState<Record<string, DockerJobs>>({});
  const [dockerJobsLoading, setDockerJobsLoading] = useAtom(dockerJobsLoadingAtom);

  // Job names being migrated, keyed by workflow path. Keyed by workflow so
  // that two workflows with a same-named job don't contaminate each other.
  const plannedMigrationsByWorkflow = useMemo(() => {
    const result: Record<string, string[]> = {};
    for (const [workflow, jobs] of Object.entries(updatedRunnerPaths)) {
      const changed = Object.entries(jobs).filter(([, runners]) => toRunnerPairs(runners).some(([current, target]) => current !== target)).map(([name]) => name);
      if (changed.length > 0) result[workflow] = changed;
    }
    return result;
  }, [updatedRunnerPaths]);
  const toggleJob = useCallback((workflow_0: string, job: string) => {
    setDockerCachingJobs(draft => {
      if (!draft[workflow_0]) {
        draft[workflow_0] = {};
      }
      draft[workflow_0][job] = !draft[workflow_0][job];
    });
  }, [setDockerCachingJobs]);

  // Enable/disable docker caching for every job in every workflow at once.
  const handleGlobalToggle = useCallback((enabled: boolean) => {
    setGlobalToggle(enabled);
    setDockerCachingJobs(draft_0 => {
      for (const workflow_1 of Object.keys(draft_0)) {
        for (const job_0 of Object.keys(draft_0[workflow_1])) {
          draft_0[workflow_1][job_0] = enabled;
        }
      }
    });
  }, [setDockerCachingJobs]);

  // Keep the global toggle in sync as individual jobs are flipped on/off.
  useEffect(() => {
    const all = Object.values(dockerCachingJobs).flatMap(Object.values);
    if (all.length === 0) return;
    if (all.every(Boolean)) setGlobalToggle(true);else if (all.every(v => !v)) setGlobalToggle(false);
  }, [dockerCachingJobs]);

  // Called once per workflow after its docker-jobs endpoint resolves.
  // Caches the raw data and initializes the per-job caching atoms to
  // the configured default (enabled for auto-migrate, disabled otherwise).
  const handleDockerDataLoaded = useCallback((workflow_2: string, data: DockerJobs) => {
    setWorkflowDockerData(prev => ({
      ...prev,
      [workflow_2]: data
    }));
    const jobNames = Object.keys(data ?? {});
    if (jobNames.length === 0) return;
    setDockerCachingJobs(draft_1 => {
      draft_1[workflow_2] ??= {};
      for (const job_1 of jobNames) {
        draft_1[workflow_2][job_1] ??= defaultEnabled;
      }
    });
  }, [setDockerCachingJobs, defaultEnabled]);

  // Track which workflows are currently loading so we only increment the
  // global docker-jobs loading counter once per workflow, even if a child
  // component reports isLoading multiple times.
  const loadingWorkflowsRef = useRef<Set<string>>(new Set());
  const handleLoadingChange = useCallback((workflow_3: string, isLoading: boolean) => {
    const loading = loadingWorkflowsRef.current;
    const wasLoading = loading.has(workflow_3);
    if (isLoading && !wasLoading) {
      loading.add(workflow_3);
      setDockerJobsLoading(count => count + 1);
    } else if (!isLoading && wasLoading) {
      loading.delete(workflow_3);
      setDockerJobsLoading(count_0 => Math.max(0, count_0 - 1));
    }
  }, [setDockerJobsLoading]);

  // Only show workflows that actually contain docker builds.
  const workflowsWithDocker = useMemo(() => {
    return Object.keys(workflowDockerData).filter(path => Object.keys(workflowDockerData[path]).length > 0).map(path_0 => ({
      path: path_0,
      name: allFiles.find(f => f.path === path_0)?.name ?? path_0.split("/").pop() ?? path_0
    }));
  }, [workflowDockerData, allFiles]);
  return <div className="flex flex-col">
      {/* Fetch Docker data for all workflow files */}
      {allFiles?.map(file => <DockerJobsFetcher key={file.path} workflowPath={file.path} repository={selectedRepository} plannedMigrations={plannedMigrationsByWorkflow[file.path] ?? []} onDataLoaded={handleDockerDataLoaded} onLoadingChange={handleLoadingChange} />)}

      <DockerCachingDiffExplainer />

      {/* Global toggle section - only show if there are Docker jobs and loading is complete */}
      {dockerJobsLoading === 0 && workflowsWithDocker.length > 0 && <div className="p-3 border-b">
          <div className="flex items-center gap-3">
            <Switch id="docker-caching-all" checked={globalToggle} onCheckedChange={handleGlobalToggle} className="data-[state=checked]:bg-green-500" />
            <label htmlFor="docker-caching-all" className="text-sm cursor-pointer">
              Enable Docker Caching for all workflows
            </label>
          </div>
        </div>}

      {/* Individual jobs selection */}
      <WorkflowAccordion type="multiple" defaultValue={workflowsWithDocker[0] ? [`docker-${workflowsWithDocker[0].path}`] : []}>
        <ScrollArea className="h-[200px] border-b w-full">
          {dockerJobsLoading > 0 && <div className="flex items-center justify-center h-[200px]">
              <LoadingSpinner />
            </div>}

          {dockerJobsLoading === 0 && error && <div className="flex items-center justify-center h-[200px] text-neutral-500">
              Failed to load jobs
            </div>}

          {dockerJobsLoading === 0 && !error && workflowsWithDocker.length === 0 && <div className="flex items-center justify-center h-[200px] text-neutral-500">
                No Docker builds found in any workflows
              </div>}

          {dockerJobsLoading === 0 && !error && workflowsWithDocker.map(workflow_4 => <DockerCachingWorkflowJobs key={workflow_4.path} workflowPath={workflow_4.path} workflowDisplayName={workflow_4.name} selectedRepository={selectedRepository} dockerCachingJobs={dockerCachingJobs[workflow_4.path] || {}} dockerJobs={workflowDockerData[workflow_4.path] || {}} toggleJob={toggleJob} />)}
        </ScrollArea>
      </WorkflowAccordion>
    </div>;
}
function DockerCachingWorkflowJobs(t0) {
  const $ = _c(24);
  const {
    workflowPath,
    workflowDisplayName,
    dockerCachingJobs,
    dockerJobs,
    toggleJob
  } = t0;
  let t1;
  bb0: {
    const jobNames = Object.keys(dockerJobs);
    if (jobNames.length === 0) {
      t1 = false;
      break bb0;
    }
    t1 = jobNames.every(jobName => dockerCachingJobs[jobName] === true);
  }
  const workflowEnabled = t1;
  let t2;
  if ($[0] !== dockerCachingJobs || $[1] !== dockerJobs || $[2] !== toggleJob || $[3] !== workflowPath) {
    t2 = enabled => {
      Object.keys(dockerJobs).forEach(jobName_0 => {
        if (dockerCachingJobs[jobName_0] !== enabled) {
          toggleJob(workflowPath, jobName_0);
        }
      });
    };
    $[0] = dockerCachingJobs;
    $[1] = dockerJobs;
    $[2] = toggleJob;
    $[3] = workflowPath;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const toggleWorkflow = t2;
  if (!dockerJobs || Object.keys(dockerJobs).length === 0) {
    return null;
  }
  const t3 = `docker-${workflowPath}`;
  const t4 = `docker-${workflowPath}`;
  let t5;
  if ($[5] !== workflowDisplayName) {
    t5 = <span className="px-2 py-1 text-sm font-medium">{workflowDisplayName}</span>;
    $[5] = workflowDisplayName;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== toggleWorkflow || $[8] !== workflowEnabled) {
    t6 = <div className="flex items-center gap-2" onClick={_temp11}><span className="text-xs text-neutral-500 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer" onClick={() => toggleWorkflow(!workflowEnabled)}>Enable caching</span><Switch checked={workflowEnabled} onCheckedChange={toggleWorkflow} className="data-[state=checked]:bg-green-500" /></div>;
    $[7] = toggleWorkflow;
    $[8] = workflowEnabled;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <WorkflowAccordionTrigger className="group cursor-pointer"><div className="flex items-center justify-between w-full pr-4">{t5}{t6}</div></WorkflowAccordionTrigger>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== dockerJobs) {
    t8 = Object.keys(dockerJobs);
    $[13] = dockerJobs;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== dockerCachingJobs || $[16] !== t8 || $[17] !== workflowPath) {
    t9 = <WorkflowAccordionContent className="flex flex-col">{t8.map(jobName_1 => <div key={`${workflowPath}-${jobName_1}`} className="flex items-center gap-3 px-4 py-2 border-b last:border-0 bg-neutral-50 dark:bg-neutral-900"><div className="flex items-center min-w-0 gap-2"><CheckCircleIcon className={cn(dockerCachingJobs[jobName_1] ? "text-green-500" : "text-neutral-400", "w-5 h-5 min-w-[20px]")} /><div className="text-sm truncate">{jobName_1}</div></div></div>)}</WorkflowAccordionContent>;
    $[15] = dockerCachingJobs;
    $[16] = t8;
    $[17] = workflowPath;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t3 || $[20] !== t4 || $[21] !== t7 || $[22] !== t9) {
    t10 = <WorkflowAccordionItem value={t3} key={t4} className="bg-neutral-200/50 dark:bg-neutral-950/40">{t7}{t9}</WorkflowAccordionItem>;
    $[19] = t3;
    $[20] = t4;
    $[21] = t7;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  return t10;
}
function _temp11(e) {
  return e.stopPropagation();
}
function StepCircle(t0) {
  const $ = _c(8);
  const {
    step,
    complete
  } = t0;
  let t1;
  if ($[0] !== complete) {
    t1 = complete && <CheckCircleIcon className="flex items-center -mx-0.5 justify-center w-6 h-6 text-green-500 rounded-full" />;
    $[0] = complete;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== complete || $[3] !== step) {
    t2 = !complete && <div className="flex items-center justify-center w-5 h-5 text-xs text-white rounded-full dark:text-black bg-neutral-400">{step}</div>;
    $[2] = complete;
    $[3] = step;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div>{t1}{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}